import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import {
  Thermometer, Refrigerator, Droplets, WashingMachine, Wind, Sparkles, Flame, Microwave, RefreshCw, AlarmSmoke, DoorClosed, Wrench,
  Sprout, SolarPanel, Waves, Fuel, Zap, Battery, ArrowDownToLine, AirVent, Pipette, Heater, PlugZap, Home, ShieldCheck, Filter,
  House, Package, CalendarDays, ClipboardList, Hammer, UserCircle, FolderOpen, FileOutput, CheckCheck,
  DollarSign, ShoppingCart, MapPin, ShoppingBag, ExternalLink, UserPlus, Leaf,
} from 'lucide-react'
import { supabase } from '../supabase'
import {
  fetchAppliancesForHome,
  fetchApplianceById,
  updateApplianceAsset,
  updateApplianceSlot,
  updateAppliancePlacement,
} from '../utils/applianceQueries'
import InviteModal from './InviteModal'
import ServiceLogModal from './ServiceLogModal'
import ContractorRequestModal from './ContractorRequestModal'
import AddAppliance from './AddAppliance'
import StandardAppliancesPrompt from './StandardAppliancesPrompt'
import ApplianceDetail from './ApplianceDetail'
import GuidedDocumentationModal from './GuidedDocumentationModal'
import HomeSetup from './HomeSetup'
import AddSystem, { systemSchedules } from './AddSystem'
import SystemDetail from './SystemDetail'
import AddService from './AddService'
import ServiceDetail from './ServiceDetail'
import SettingsPanel from './SettingsPanel'
import DocumentVault from './DocumentVault'
import ContractorDirectory, { ITEM_TO_CONTRACTOR_CAT } from './ContractorDirectory'
import AddressAutocomplete from './AddressAutocomplete'
import ScoreModal from './ScoreModal'
import PrintSummary from './PrintSummary'
import MaintenanceTaskTile from './MaintenanceTaskTile'
import MaintenanceTaskModal from './MaintenanceTaskModal'
import RoomDetail from './RoomDetail'
import AddRoom from './AddRoom'
import EmergencyContacts from './EmergencyContacts'
import EmergencySheetWizard from './onboarding/EmergencySheetWizard'
import TourLauncher from '../tour/components/TourLauncher'
import { useTour } from '../tour/useTour'
import { shouldFireOnSignup } from '../tour/triggers'
import { onTourEvent, TOUR_EVENT_TYPES, TOUR_ACTIONS } from '../lib/appEvents'
import CasavioLogo from './shared/CasavioLogo'
import { VERSION_LABEL } from '../utils/version'
import { resolveDisplayAddress } from '../utils/resolveDisplayAddress'
import CollapsibleTile from './shared/CollapsibleTile'
import Onboarding from './Onboarding'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = "Georgia, serif"
const AFFILIATE_TAG = 'casavio-20'

const CountyPill = ({ enrichedAt }) => {
  const tip = enrichedAt
    ? `Verified from county records on ${new Date(enrichedAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}`
    : 'Verified from county records'
  return (
    <span
      title={tip}
      style={{ display: 'inline-block', background: '#FBF3E8', color: '#2C1D14', fontSize: '10px', fontFamily: 'Calibri, sans-serif', fontWeight: '600', borderRadius: '8px', padding: '2px 7px', marginLeft: '6px', verticalAlign: 'middle', letterSpacing: '0.3px' }}
    >
      ✓ County
    </span>
  )
}

function getCountyPortalUrl(countySource, address, city, state, zip) {
  const addr = (address || '').trim()
  if (countySource === 'marin-ca') {
    return `https://apps.marincounty.gov/BOSDistrictLookup/Search/Detail?Address=${encodeURIComponent(addr)}`
  }
  if (countySource === 'douglas-nv') {
    return `https://douglasnv-search.gsacorp.io/search/u/${encodeURIComponent(addr.toUpperCase())}`
  }
  if (countySource === 'hawaii-hi') {
    return `https://qpublic.schneidercorp.com/Application.aspx?App=HawaiiCountyHI&Layer=Parcels&PageType=Search`
  }
  const query = `${city || ''} ${state || ''} county assessor records ${addr}`
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`
}

const TILE_CATEGORIES = [
  { id: 'HVAC', label: 'HVAC', emoji: '❄️', color: '#E8F4FD', listingType: 'contractor' },
  { id: 'Plumbing', label: 'Plumbing', emoji: '🚰', color: '#EBF5EF', listingType: 'contractor' },
  { id: 'Electrical', label: 'Electrical', emoji: '⚡', color: '#FBF3E8', listingType: 'contractor' },
  { id: 'Roofing', label: 'Roofing', emoji: '🏠', color: '#FDF3E8', listingType: 'contractor' },
  { id: 'Landscaping', label: 'Landscaping', emoji: '🌿', color: '#EBF5EF', listingType: 'contractor' },
  { id: 'Appliance Repair', label: 'Appliance Repair', emoji: '🔌', color: '#F3F0FC', listingType: 'contractor' },
  { id: 'Handyman', label: 'Handyman', emoji: '🔨', color: '#FEF9E8', listingType: 'contractor' },
  { id: 'Housekeeping', label: 'Housekeeping', emoji: '🧹', color: '#FCE8F3', listingType: 'service' },
  { id: 'Pest Control', label: 'Pest Control', emoji: '🪲', color: '#E8FDF0', listingType: 'service' },
  { id: 'Pool Service', label: 'Pool Service', emoji: '🏊', color: '#E8F4FD', listingType: 'service' },
  { id: 'Window Cleaning', label: 'Window Cleaning', emoji: '🪟', color: '#F0F8FF', listingType: 'service' },
  { id: 'Gutter Cleaning', label: 'Gutter Cleaning', emoji: '🍂', color: '#E8EDF5', listingType: 'service' },
  { id: 'General Contractor', label: 'General Contractor', emoji: '🏗️', color: '#F5EDE8', listingType: 'contractor' },
  { id: 'General', label: 'General', emoji: '🔧', color: '#F2EDE4', listingType: 'contractor' },
]

const FIRE_SAFETY_ITEMS = [
  { id: 'fs_zone1_debris', category: 'zone1', label: 'Dead plants and dry leaves removed from yard', required: true, info: 'Remove all dead vegetation within 0–30 feet of your home. This is the most critical zone — embers can ignite dry material instantly.', contractorItem: true },
  { id: 'fs_zone1_lawn', category: 'zone1', label: 'Lawn mowed and irrigated', required: true, info: 'Keep grass at 4 inches or shorter and irrigated during fire season. Dry grass is a primary ember ignition source.' },
  { id: 'fs_zone1_woodpile', category: 'zone1', label: 'Wood piles moved 30+ feet from home', required: true, info: 'Store firewood and lumber at least 30 feet from your home. Cover with a non-combustible cover and elevate off the ground.' },
  { id: 'fs_zone1_mulch', category: 'zone1', label: 'Non-combustible mulch within 5 feet of home', required: false, info: 'Within 5 feet of your home, use non-combustible materials like gravel or decomposed granite instead of wood mulch.', contractorItem: true },
  { id: 'fs_zone2_grass', category: 'zone2', label: 'Grass cut to 4 inches or less (30–100ft zone)', required: true, info: 'California law requires vegetation management in the 30–100 foot zone. Grass over 4" significantly increases fire spread.', contractorItem: true },
  { id: 'fs_zone2_trees', category: 'zone2', label: 'Tree branches trimmed 10 feet from ground', required: true, info: 'Limbing trees to 10 feet prevents fire climbing from grass into the tree canopy ("ladder fuels"). Required under California law.', contractorItem: true },
  { id: 'fs_zone2_shrubs', category: 'zone2', label: 'Shrubs spaced 10 feet apart', required: false, info: 'Maintain spacing between shrubs to slow fire spread. Avoid plant groupings that create a fire highway toward your home.', contractorItem: true },
  { id: 'fs_gutters', category: 'structure', label: 'Gutters and roof clear of debris', required: true, info: 'Clogged gutters filled with pine needles are a primary ember ignition point. Clean at least twice a year in fire-prone areas.', contractorItem: true },
  { id: 'fs_vents', category: 'structure', label: 'Vents screened with 1/8" metal mesh', required: false, info: 'Unscreened vents are the #1 entry point for embers. Replace with corrosion-resistant metal mesh of 1/8" or smaller.', contractorItem: true, wishlist: true },
  { id: 'fs_deck', category: 'structure', label: 'Combustible materials cleared under deck', required: false, info: 'Embers collect under decks and ignite stored items. Keep the area under your deck clear of leaves, furniture, and stored items.' },
  { id: 'fs_smoke', category: 'detection', label: 'Smoke detectors tested — all working', required: true, info: 'Test smoke detectors monthly. Replace batteries annually. California law requires alarms within 15 feet of each bedroom.' },
  { id: 'fs_co', category: 'detection', label: 'CO detectors tested — all working', required: true, info: 'Carbon monoxide detectors required in California within 4 feet of bedroom doors. Test monthly. Replace every 5–7 years.' },
  { id: 'fs_extinguisher', category: 'detection', label: 'Fire extinguisher present and charged', required: true, info: 'Have at least one ABC-rated extinguisher in the kitchen. Check the pressure gauge — needle should be in the green zone.' },
  { id: 'fs_sticker', category: 'emergency', label: 'Emergency window sticker visible from street', required: false, info: 'Alert first responders to people or pets inside. Stickers should list the number and type of occupants per floor.', appLink: 'https://www.amazon.com/s?k=emergency+window+sticker+fire&tag=casavio-20' },
  { id: 'fs_gobag', category: 'emergency', label: 'Go bag prepared with 3-day supplies', required: false, info: "Keep a bag ready: water (1 gal/person/day), food, medications, flashlight, phone charger, cash, copies of IDs, insurance docs, and pet supplies." },
  { id: 'fs_evacuation', category: 'emergency', label: 'Evacuation route planned', required: false, info: 'Plan two exit routes from your neighborhood. Know your county evacuation zone. Sign up for emergency alerts at zonehaven.com.' },
  { id: 'fs_documents', category: 'emergency', label: 'Important documents backed up in vault', required: false, info: 'Upload scans of: insurance policy, deed, passports, wills, financial accounts. Use Casavio Document Vault for secure cloud storage.' },
  { id: 'fs_inventory', category: 'insurance', label: 'Home inventory updated this year', required: false, info: 'Document your possessions with photos and receipts. Store copies in Casavio and cloud storage. Simplifies insurance claims dramatically.' },
  { id: 'fs_insurance', category: 'insurance', label: 'Insurance policy reviewed for fire coverage', required: false, info: 'Review your policy annually. Verify replacement cost (not actual cash value). Check if you are in a FAIR plan area due to wildfire risk.' },
  { id: 'fs_photos', category: 'insurance', label: 'Photos/video of home exterior taken this year', required: false, info: 'Walk around and photograph every room, closet, garage, and exterior. Store in cloud + keep a printed copy offsite.' },
]

const FIRE_CATEGORIES = {
  zone1: { label: 'Zone 1 — 0 to 30 feet', emoji: '🔥' },
  zone2: { label: 'Zone 2 — 30 to 100 feet', emoji: '🌿' },
  structure: { label: 'Structure Hardening', emoji: '🏠' },
  detection: { label: 'Detection & Suppression', emoji: '🚨' },
  emergency: { label: 'Emergency Preparedness', emoji: '🎒' },
  insurance: { label: 'Insurance & Documentation', emoji: '📋' },
}

const JOB_CATEGORIES = [
  { id: 'plumbing', label: 'Plumbing', emoji: '🔧' },
  { id: 'electrical', label: 'Electrical', emoji: '⚡' },
  { id: 'hvac', label: 'HVAC', emoji: '❄️' },
  { id: 'landscaping', label: 'Landscaping', emoji: '🌿' },
  { id: 'security', label: 'Security & Access', emoji: '🔐' },
  { id: 'accessibility', label: 'Aging in Place', emoji: '♿' },
  { id: 'cosmetic', label: 'Cosmetic Updates', emoji: '🎨' },
  { id: 'structural', label: 'Structural', emoji: '🏗️' },
  { id: 'smart_home', label: 'Smart Home', emoji: '🤖' },
  { id: 'energy', label: 'Energy Efficiency', emoji: '☀️' },
  { id: 'outdoor', label: 'Outdoor', emoji: '🌳' },
  { id: 'other', label: 'Other', emoji: '🔨' },
]

const URGENCY_CONFIG = {
  emergency: { emoji: '🔴', label: 'Emergency', color: '#DC2626', bg: '#FFF0F0' },
  planned: { emoji: '🟡', label: 'Planned', color: '#B8721F', bg: '#FFF8E7' },
  wishlist: { emoji: '⭐', label: 'Wishlist', color: '#7C3AED', bg: '#F5F3FF' },
  someday: { emoji: '💭', label: 'Someday', color: '#9A7B68', bg: '#F2EDE4' },
}

const JOB_CAT_TO_CONTRACTOR = {
  plumbing: 'Plumbing', electrical: 'Electrical', hvac: 'HVAC',
  landscaping: 'Landscaping', structural: 'General Contractor',
  cosmetic: 'Handyman', smart_home: 'Handyman', energy: 'General',
  outdoor: 'Landscaping', security: 'General', accessibility: 'Handyman', other: 'General',
}

const maintenanceSchedules = {
  hvac: [
    { task: 'Filter Replacement', interval: 'Every 90 days', type: 'auto_order', partCost: '$15–$40' },
    { task: 'Coil Cleaning', interval: 'Annually — Spring', type: 'contractor' },
    { task: 'Full System Inspection', interval: 'Annually — Fall', type: 'contractor' },
  ],
  refrigerator: [
    { task: 'Water Filter Replacement', interval: 'Every 6 months', type: 'auto_order', partCost: '$25–$55' },
    { task: 'Condenser Coil Cleaning', interval: 'Annually', type: 'diy' },
    { task: 'Door Seal Inspection', interval: 'Annually', type: 'diy' },
  ],
  water_heater: [
    { task: 'Tank Flush & Sediment Drain', interval: 'Annually', type: 'diy' },
    { task: 'Anode Rod Inspection', interval: 'Every 2 years', type: 'contractor' },
    { task: 'Pressure Relief Valve Test', interval: 'Annually', type: 'diy' },
  ],
  washer: [
    { task: 'Drum Cleaning Cycle', interval: 'Monthly', type: 'auto_order', partCost: '$8–$15' },
    { task: 'Door Gasket Wipe Down', interval: 'Monthly', type: 'diy' },
    { task: 'Inlet Hose Inspection', interval: 'Annually', type: 'diy' },
  ],
  dryer: [
    { task: 'Lint Trap Cleaning', interval: 'Every use', type: 'diy' },
    { task: 'Vent Duct Cleaning', interval: 'Annually', type: 'contractor' },
    { task: 'Exterior Vent Check', interval: 'Every 6 months', type: 'diy' },
  ],
  dishwasher: [
    { task: 'Filter Cleaning', interval: 'Monthly', type: 'diy' },
    { task: 'Spray Arm Cleaning', interval: 'Every 3 months', type: 'diy' },
    { task: 'Descaling Treatment', interval: 'Every 6 months', type: 'auto_order', partCost: '$8–$18' },
  ],
  smoke_detector: [
    { task: 'Battery Replacement', interval: 'Annually', type: 'auto_order', partCost: '$5–$12' },
    { task: 'Test & Inspection', interval: 'Monthly', type: 'diy' },
    { task: 'Full Replacement', interval: 'Every 10 years', type: 'auto_order', partCost: '$20–$50' },
  ],
  garage_door: [
    { task: 'Lubricate Moving Parts', interval: 'Every 6 months', type: 'auto_order', partCost: '$8–$15' },
    { task: 'Safety Sensor Test', interval: 'Monthly', type: 'diy' },
    { task: 'Full Service Inspection', interval: 'Annually', type: 'contractor' },
  ],
  oven: [
    { task: 'Deep Clean', interval: 'Every 3 months', type: 'diy' },
    { task: 'Burner Inspection', interval: 'Annually', type: 'diy' },
  ],
  microwave: [
    { task: 'Interior Clean', interval: 'Monthly', type: 'diy' },
    { task: 'Filter Replacement', interval: 'Every 6 months', type: 'auto_order', partCost: '$10–$25' },
  ],
  garbage_disposal: [
    { task: 'Cleaning & Deodorizing', interval: 'Monthly', type: 'auto_order', partCost: '$5–$12' },
    { task: 'Blade Inspection', interval: 'Annually', type: 'diy' },
  ],
  other: [{ task: 'General Inspection', interval: 'Annually', type: 'diy' }],
}

function calculateScore(profile, appliances, systems) {
  let score = 0
  const profileFields = ['address', 'city', 'state', 'zip', 'property_type', 'year_built', 'bedrooms', 'bathrooms', 'sqft']
  const filledProfile = profileFields.filter(f => profile?.[f]).length
  score += Math.round((filledProfile / profileFields.length) * 25)
  const totalItems = (appliances?.length || 0) + (systems?.length || 0)
  score += Math.min(25, Math.round((totalItems / 5) * 25))
  const totalAppliances = appliances?.length || 0
  if (totalAppliances > 0) {
    const goodCount = appliances.filter(a => a.status === 'good').length
    score += Math.round((goodCount / totalAppliances) * 50)
  } else if (profile?.address) {
    score += 15
  }
  return Math.min(100, score)
}

function getLetterGrade(score) {
  if (score >= 93) return 'A+'
  if (score >= 90) return 'A'
  if (score >= 87) return 'A-'
  if (score >= 83) return 'B+'
  if (score >= 80) return 'B'
  if (score >= 77) return 'B-'
  if (score >= 73) return 'C+'
  if (score >= 70) return 'C'
  if (score >= 67) return 'C-'
  if (score >= 63) return 'D+'
  if (score >= 60) return 'D'
  return 'F'
}

function getScoreColor(score) {
  if (score >= 80) return { primary: '#2E6B4A', light: '#EBF5EF' }
  if (score >= 60) return { primary: C.amber, light: '#FBF3E8' }
  return { primary: C.danger, light: '#FDECEA' }
}

function calcMaintenanceNextDue(lastDate, interval) {
  if (!lastDate || !interval) return null
  const d = new Date(lastDate)
  const i = interval.toLowerCase()
  if (i.includes('every use') || i.includes('each use')) return null
  if (i.includes('month') && i.includes('3')) d.setDate(d.getDate() + 90)
  else if (i.includes('month') && i.includes('6')) d.setDate(d.getDate() + 180)
  else if (i.includes('month')) d.setDate(d.getDate() + 30)
  else if (i.includes('90')) d.setDate(d.getDate() + 90)
  else if (i.includes('2 year')) d.setDate(d.getDate() + 730)
  else if (i.includes('year') || i.includes('annual')) d.setDate(d.getDate() + 365)
  else if (i.includes('180')) d.setDate(d.getDate() + 180)
  else if (i.includes('week')) d.setDate(d.getDate() + 7)
  else return null
  return d.toISOString().split('T')[0]
}

function getUpcomingStatus(nextDue) {
  if (!nextDue) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((new Date(nextDue) - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays <= 30) return 'soon'
  if (diffDays <= 90) return 'upcoming'
  return 'good'
}

function formatLogDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

function getGreeting() {
  const hour = new Date().getHours()
  if (hour >= 5 && hour < 12) return 'Good morning'
  if (hour >= 12 && hour < 17) return 'Good afternoon'
  return 'Good evening'
}

// Smart rotating subtitle messages
function getSmartSubtitle(appliances, systems, allScheduleItems) {
  const messages = []
  const autoOrderCount = allScheduleItems.filter(i => i.type === 'auto_order').length
  const contractorCount = allScheduleItems.filter(i => i.type === 'contractor').length
  const overdueAppliances = appliances.filter(a => a.status === 'overdue')
  const dueAppliances = appliances.filter(a => a.status === 'due')

  if (overdueAppliances.length > 0) {
    messages.push(`${overdueAppliances.length} appliance${overdueAppliances.length !== 1 ? 's' : ''} overdue for service`)
  } else if (dueAppliances.length > 0) {
    messages.push(`${dueAppliances.length} appliance${dueAppliances.length !== 1 ? 's' : ''} due for service`)
  } else if (autoOrderCount > 0) {
    messages.push(`${autoOrderCount} part${autoOrderCount !== 1 ? 's' : ''} available to order`)
  } else if (contractorCount > 0) {
    messages.push(`${contractorCount} contractor task${contractorCount !== 1 ? 's' : ''} to schedule`)
  } else if (appliances.length > 0) {
    messages.push(`${appliances.length} appliance${appliances.length !== 1 ? 's' : ''} · ${allScheduleItems.length} maintenance task${allScheduleItems.length !== 1 ? 's' : ''}`)
  } else {
    messages.push("Add your first appliance to get started")
  }
  return messages[0]
}

const applianceIcons = {
  hvac: Thermometer, refrigerator: Refrigerator, water_heater: Droplets,
  washer: WashingMachine, dryer: Wind, dishwasher: Sparkles,
  oven: Flame, microwave: Microwave, garbage_disposal: RefreshCw,
  smoke_detector: AlarmSmoke, garage_door: DoorClosed, other: Wrench,
}
const systemIcons = {
  septic: Sprout, solar: SolarPanel, pool: Waves, well: Droplets,
  propane: Fuel, generator: Zap, battery: Battery,
  sump_pump: ArrowDownToLine, whole_house_fan: AirVent, water_softener: Pipette,
  radiant_heat: Heater, electrical_panel: PlugZap, adu: Home, other: Wrench,
  fireplace: Flame, fire_sprinkler: ShieldCheck, air_filtration: Filter,
}
function getItemIcon(itemType, sourceType) {
  const map = itemType === 'appliance' ? applianceIcons : systemIcons
  return map[sourceType] || Wrench
}

const tabs = [
  { id: 'home', label: 'Home', Icon: House, enabled: true },
  { id: 'home_profile', label: 'Home Profile', Icon: Package, enabled: true },
  { id: 'maintenance', label: 'Maintenance', Icon: CalendarDays, enabled: true },
  { id: 'services', label: 'Services', Icon: ClipboardList, enabled: true },
]

const propertyTypeLabels = {
  single_family: 'Single Family Home', condo: 'Condo / Townhome',
  multi_family: 'Multi-Family', mobile: 'Mobile Home',
  vacation: 'Vacation Home', other: 'Property',
}

const RELATION_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'renter', label: 'Renter' },
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'family_member', label: 'Family Member' },
  { value: 'caretaker', label: 'Caretaker' },
  { value: 'guest', label: 'Guest' },
]

const S = {
  container: { minHeight: '100vh', background: C.bg, fontFamily: f, overflowX: 'hidden' },
  header: {
    background: C.card, borderBottom: `1px solid ${C.border}`,
    padding: '16px 24px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  },
  logo: { fontSize: '24px', fontWeight: '700', color: C.amber, letterSpacing: '3px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: '700', fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer', boxShadow: '0 2px 8px rgba(184,114,31,0.3)',
  },
  signOut: {
    background: 'transparent', border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '7px 14px', cursor: 'pointer',
    fontSize: '12px', color: C.muted, fontFamily: f, letterSpacing: '1px',
  },
  propertyBar: {
    background: '#FBF3E8', borderBottom: `1px solid ${C.amber}22`,
    padding: '0 24px', display: 'flex', justifyContent: 'center',
  },
  propertyBarInner: {
    maxWidth: '1100px', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0',
  },
  propertySelector: {
    display: 'flex', alignItems: 'center', gap: '8px',
    cursor: 'pointer', userSelect: 'none',
  },
  propertyAddress: { fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', fontWeight: '600' },
  propertyAddressEmpty: { fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer' },
  propertyChevron: { fontSize: '10px', color: C.muted, marginLeft: '2px' },
  propertyType: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  propertyDropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '12px', boxShadow: '0 8px 32px rgba(44,29,20,0.15)',
    zIndex: 500, overflow: 'hidden', marginTop: '4px',
  },
  propertyDropdownItem: (active) => ({
    padding: '14px 16px', cursor: 'pointer',
    background: active ? '#FBF3E8' : 'transparent',
    borderBottom: `1px solid ${C.border}`, transition: 'background 0.15s',
  }),
  propertyDropdownItemName: { fontSize: '14px', fontWeight: '600', color: C.brown, fontFamily: 'Calibri, sans-serif' },
  propertyDropdownItemAddress: { fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
  propertyDropdownAdd: { padding: '14px 16px', cursor: 'default', opacity: 0.5, display: 'flex', alignItems: 'center', gap: '8px' },
  propertyDropdownAddText: { fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  propertyDropdownComingSoon: { fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  tabBar: {
    background: C.card, borderBottom: `1px solid ${C.border}`,
    display: 'flex', justifyContent: 'center', padding: '0',
  },
  tabBarInner: { display: 'flex', maxWidth: '1100px', width: '100%' },
  tab: (active, enabled) => ({
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '10px 0 8px', cursor: enabled ? 'pointer' : 'default',
    borderBottom: active ? `2px solid ${C.amber}` : '2px solid transparent',
    transition: 'all 0.2s', opacity: enabled ? 1 : 0.4, userSelect: 'none',
  }),
  tabIcon: { display: 'flex', justifyContent: 'center', marginBottom: '3px' },
  tabLabel: (active) => ({
    fontSize: '10px', letterSpacing: '0', textTransform: 'uppercase',
    fontFamily: 'Calibri, sans-serif', fontWeight: active ? '700' : '400',
    color: active ? C.amber : C.muted,
  }),
  content: { padding: '24px 16px 48px', maxWidth: '1100px', margin: '0 auto' },
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif',
  },
  setupBanner: {
    background: '#FBF3E8', border: `1px solid ${C.amber}44`,
    borderRadius: '12px', padding: '16px 20px', marginBottom: '24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
  },
  setupBannerText: { fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif' },
  setupBannerBtn: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '8px', padding: '9px 18px',
    color: C.card, fontSize: '12px', fontWeight: '700',
    letterSpacing: '1px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: f, whiteSpace: 'nowrap',
  },
  addBtn: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '12px 20px',
    color: C.card, fontSize: '13px', fontWeight: '700',
    letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: f,
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px', marginBottom: '20px',
  },
  addCard: {
    background: 'transparent', border: `1px dashed ${C.border}`,
    borderRadius: '12px', padding: '16px', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '120px',
  },
  emptyCard: {
    background: C.card, border: `1px dashed ${C.border}`,
    borderRadius: '16px', padding: '48px 32px', textAlign: 'center',
    boxShadow: '0 2px 8px rgba(44,29,20,0.05)',
  },
  appCard: (status) => ({
    position: 'relative',
    background: C.card,
    border: `1px solid ${status === 'good' ? C.border : status === 'due' ? C.amber + '55' : C.danger + '55'}`,
    borderRadius: '12px', padding: '14px', cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(44,29,20,0.05)', transition: 'all 0.2s',
  }),
  incompleteBadge: {
    position: 'absolute', bottom: '8px', right: '8px',
    border: `1px solid ${C.amberLight}`, background: C.card, color: C.brown,
    fontFamily: 'Calibri, sans-serif',
    padding: '6px 10px', borderRadius: '10px',
    lineHeight: '1.2', whiteSpace: 'nowrap',
    textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1px',
  },
  incompleteBadgeTop: { fontSize: '12px', fontWeight: 700, color: C.brown },
  incompleteBadgeBottom: { fontSize: '11px', fontStyle: 'italic', color: C.brown, opacity: 0.85 },
  tag: (type) => {
    const colors = {
      auto_order: { bg: '#FBF3E8', color: '#B8721F' },
      contractor: { bg: '#EBF5EF', color: '#2E6B4A' },
      diy: { bg: '#F2EDE4', color: '#9A7B68' },
    }
    const c = colors[type] || colors.diy
    return {
      fontSize: '9px', padding: '2px 8px', borderRadius: '20px',
      background: c.bg, color: c.color,
      fontFamily: 'Calibri, sans-serif', border: `1px solid ${c.color}44`,
      whiteSpace: 'nowrap', flexShrink: 0,
    }
  },
  serviceStatusBadge: (status) => {
    const colors = {
      active: { bg: '#EBF5EF', color: C.success }, scheduled: { bg: '#EBF5EF', color: C.success },
      'in progress': { bg: '#FBF3E8', color: C.amber }, completed: { bg: '#EBF5EF', color: C.success },
      'on hold': { bg: '#FBF3E8', color: C.amber }, cancelled: { bg: '#FDECEA', color: C.danger },
    }
    const c = colors[status?.toLowerCase()] || colors.active
    return { fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: c.bg, color: c.color, fontFamily: 'Calibri, sans-serif', border: `1px solid ${c.color}44`, display: 'inline-block' }
  },
}

function FireSafetyItem({ item, checked, isLast, onToggle, onAddWishlist }) {
  const [showInfo, setShowInfo] = useState(false)
  return (
    <div style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}`, paddingBottom: isLast ? 0 : 12, marginBottom: isLast ? 0 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }} onClick={onToggle}>
        <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${checked ? C.amber : C.border}`, background: checked ? C.amber : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, transition: 'all 0.15s' }}>
          {checked && <span style={{ color: '#fff', fontSize: 13, lineHeight: 1 }}>✓</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: C.brown, lineHeight: 1.4 }}>{item.label}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            {item.required && <span style={{ fontSize: 10, color: '#DC2626', background: '#FFF0F0', border: '1px solid #DC262644', borderRadius: 4, padding: '1px 6px' }}>CA Law</span>}
            {item.contractorItem && (
              <button onClick={e => { e.stopPropagation(); onAddWishlist() }} style={{ background: 'none', border: `1px solid ${C.amber}`, borderRadius: 4, padding: '1px 8px', fontSize: 10, color: C.amber, cursor: 'pointer' }}>
                ＋ Wishlist
              </button>
            )}
            {item.appLink && (
              <a href={item.appLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, color: C.amber, textDecoration: 'none', border: `1px solid ${C.amber}44`, borderRadius: 4, padding: '1px 8px' }}>
                Shop →
              </a>
            )}
            {item.info && (
              <button onClick={e => { e.stopPropagation(); setShowInfo(p => !p) }} style={{ background: 'none', border: 'none', padding: '1px 4px', fontSize: 14, color: showInfo ? C.amber : C.muted, cursor: 'pointer', lineHeight: 1 }}>ℹ</button>
            )}
          </div>
        </div>
      </div>
      {showInfo && item.info && (
        <div style={{ fontSize: 12, color: C.muted, background: C.bg, borderRadius: 8, padding: '10px 12px', marginTop: 8, marginLeft: 34, lineHeight: 1.5 }}>
          {item.info}
        </div>
      )}
    </div>
  )
}

function getTaskCategory(taskName) {
  const n = (taskName || '').toLowerCase()
  if (n.includes('hvac') || n.includes('coil') || n.includes('duct') || n.includes('filter')) return 'hvac'
  if (n.includes('plumb') || n.includes('water') || n.includes('anode') || n.includes('pressure')) return 'plumbing'
  if (n.includes('electric') || n.includes('panel') || n.includes('outlet')) return 'electrical'
  if (n.includes('roof') || n.includes('gutter') || n.includes('vent')) return 'structural'
  if (n.includes('inspect') || n.includes('service') || n.includes('clean')) return 'other'
  return 'other'
}

function getCounty(home) {
  const city = (home?.city || '').toLowerCase()
  const marin = ['san rafael', 'novato', 'mill valley', 'sausalito', 'tiburon', 'corte madera', 'larkspur', 'san anselmo', 'fairfax', 'ross', 'greenbrae', 'kentfield']
  const sonoma = ['santa rosa', 'petaluma', 'rohnert park', 'healdsburg', 'sebastopol', 'sonoma', 'windsor', 'cloverdale', 'cotati']
  const napa = ['napa', 'calistoga', 'st. helena', 'yountville', 'american canyon']
  const solano = ['vallejo', 'fairfield', 'vacaville', 'benicia', 'dixon', 'suisun']
  if (marin.some(c => city.includes(c))) return 'marin'
  if (sonoma.some(c => city.includes(c))) return 'sonoma'
  if (napa.some(c => city.includes(c))) return 'napa'
  if (solano.some(c => city.includes(c))) return 'solano'
  return null
}

function getCountyRecordsLinks(county) {
  const links = {
    marin: [
      { label: 'Assessor / Property Records', url: 'https://www.marincounty.org/depts/ar/divisions/assessor' },
      { label: 'Recorder / Deeds & Liens', url: 'https://www.marincounty.org/depts/ar/divisions/recorder' },
      { label: 'Building Permits', url: 'https://cw.marincounty.org/main/CW/Default.aspx' },
    ],
    sonoma: [
      { label: 'Assessor / Property Records', url: 'https://sonomacounty.ca.gov/administrative-support-and-fiscal-services/assessors-office' },
      { label: 'Recorder / Deeds & Liens', url: 'https://sonomacounty.ca.gov/administrative-support-and-fiscal-services/clerk-recorder-assessor/recorder' },
      { label: 'Building Permits', url: 'https://sonomacounty.ca.gov/business-and-environment/permit-sonoma' },
    ],
    napa: [
      { label: 'Assessor / Property Records', url: 'https://www.countyofnapa.org/171/Assessor' },
      { label: 'Recorder / Deeds & Liens', url: 'https://www.countyofnapa.org/229/Clerk-Recorder' },
      { label: 'Building Permits', url: 'https://www.countyofnapa.org/1139/Building-Permits' },
    ],
    solano: [
      { label: 'Assessor / Property Records', url: 'https://www.solanocounty.com/depts/assessor/default.asp' },
      { label: 'Recorder / Deeds & Liens', url: 'https://www.solanocounty.com/depts/clerkrecorder/default.asp' },
      { label: 'Building Permits', url: 'https://www.solanocounty.com/depts/rm/building/default.asp' },
    ],
  }
  return county ? (links[county] || null) : null
}

const WISHLIST_SUGGESTIONS = [
  { jobTitle: 'Install locking mailbox', category: 'security', label: 'Locking mailbox', desc: 'USPS-approved locking mailbox for mail security', estLow: 100, estHigh: 350 },
  { jobTitle: 'Install EV charging outlet in garage', category: 'energy', label: 'EV charger', desc: 'Level 2 EVSE outlet for electric vehicles', estLow: 500, estHigh: 1500 },
  { jobTitle: 'Install grab bars in bathrooms', category: 'accessibility', label: 'Grab bars', desc: 'ADA-compliant grab bars for aging in place', estLow: 200, estHigh: 600 },
  { jobTitle: 'Install smart thermostat', category: 'smart_home', label: 'Smart thermostat', desc: 'Programmable thermostat for energy savings', estLow: 150, estHigh: 300 },
  { jobTitle: 'Install outdoor security camera', category: 'security', label: 'Security camera', desc: 'Motion-activated outdoor camera system', estLow: 200, estHigh: 800 },
  { jobTitle: 'Install coded/smart front door lock', category: 'security', label: 'Smart lock', desc: 'Keypad or smart lock for entry convenience', estLow: 150, estHigh: 400 },
]

export default function Dashboard({ session }) {
  const [activeTab, setActiveTab] = useState('home')
  const [appliances, setAppliances] = useState([])
  const [systems, setSystems] = useState([])
  const [services, setServices] = useState([])
  const [profile, setProfile] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [addInitialType, setAddInitialType] = useState(null) // pre-select a type when opening Add Appliance from Standard Appliances prompt
  const [showAddSystem, setShowAddSystem] = useState(false)
  const [showAddService, setShowAddService] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showScoreModal, setShowScoreModal] = useState(false)
  const [showEnergyModal, setShowEnergyModal] = useState(false)
  const [wishlistToast, setWishlistToast] = useState(null)
  const [showFireSafety, setShowFireSafety] = useState(false)
  const [activeFSCategory, setActiveFSCategory] = useState(null)
  const [fireChecklist, setFireChecklist] = useState(null)
  const [fireCompletedItems, setFireCompletedItems] = useState([])
  const [fireSaving, setFireSaving] = useState(false)
  const [showPrint, setShowPrint] = useState(false)
  const [selectedAppliance, setSelectedAppliance] = useState(null)
  const [guidedAppliance, setGuidedAppliance] = useState(null)
  const [selectedSystem, setSelectedSystem] = useState(null)
  const [selectedService, setSelectedService] = useState(null)
  const [loading, setLoading] = useState(true)
  const [systemsLoading, setSystemsLoading] = useState(true)
  const [servicesLoading, setServicesLoading] = useState(true)
  const [profileChecked, setProfileChecked] = useState(false)
  const [showSetup, setShowSetup] = useState(false)
  const [profileIncomplete, setProfileIncomplete] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false)
  const [properties, setProperties] = useState([])
  const [activePropertyId, setActivePropertyId] = useState(null)
  const [showAddProperty, setShowAddProperty] = useState(false)
  const [homes, setHomes] = useState([])
  const [activeHomeId, setActiveHomeId] = useState(null)
  const [maintenanceLogs, setMaintenanceLogs] = useState([])
  const [maintenanceLogsLoading, setMaintenanceLogsLoading] = useState(true)
  const [dueParts, setDueParts] = useState([])
  const [duePartsLoading, setDuePartsLoading] = useState(true)
  const [orderedParts, setOrderedParts] = useState({})
  const [showAllLogs, setShowAllLogs] = useState(false)
  const [showDocumentVault, setShowDocumentVault] = useState(false)
  const [showContractorDir, setShowContractorDir] = useState(false)
  const [userRelation, setUserRelation] = useState('owner')
  const [serviceLogs, setServiceLogs] = useState([])
  const [logService, setLogService] = useState(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [requestContractor, setRequestContractor] = useState(null)
  const [homeProfile, setHomeProfile] = useState(null)
  const [homeProfileForm, setHomeProfileForm] = useState({})
  const [editingProfile, setEditingProfile] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)
  const [profileSnapshot, setProfileSnapshot] = useState(null) // form state at edit-open, for dirty/discard tracking
  const [countyEnrichLoading, setCountyEnrichLoading] = useState(false)
  const [countyEnrichBanner, setCountyEnrichBanner] = useState(null)
  const [rooms, setRooms] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [showAddRoom, setShowAddRoom] = useState(false)
  const [editingExterior, setEditingExterior] = useState(false)
  const [exteriorForm, setExteriorForm] = useState({})
  const [exteriorSaving, setExteriorSaving] = useState(false)
  const [showEmergencyContacts, setShowEmergencyContacts] = useState(false)
  const [showEmergencyWizard, setShowEmergencyWizard] = useState(false)
  const [showEmergencyPromo, setShowEmergencyPromo] = useState(false)
  const [homeDetailsExpanded, setHomeDetailsExpanded] = useState(false)
  const [exteriorExpanded, setExteriorExpanded] = useState(false)
  const [interiorNotesExpanded, setInteriorNotesExpanded] = useState(false)
  const [countyRecordsExpanded, setCountyRecordsExpanded] = useState(false)
  const [showProblemReport, setShowProblemReport] = useState(false)
  const [problemCategory, setProblemCategory] = useState('')
  const [problemDescription, setProblemDescription] = useState('')
  const [problemSubmitting, setProblemSubmitting] = useState(false)
  const [contractorDirCategory, setContractorDirCategory] = useState('')
  const [contractorDirListingType, setContractorDirListingType] = useState('contractor')
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 720)
  const propertyDropdownRef = useRef(null)

  // Jobs system
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobFilter, setJobFilter] = useState('all')
  const [showAddJob, setShowAddJob] = useState(false)
  const [editingJob, setEditingJob] = useState(null)
  const [jobStep, setJobStep] = useState(1)
  const [jobData, setJobData] = useState({ urgency: 'wishlist', priority: 'medium' })
  const [wishlistItems, setWishlistItems] = useState([])
  const [selectedJob, setSelectedJob] = useState(null)
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const [showTourLauncher, setShowTourLauncher] = useState(false)
  const [isBetaUser, setIsBetaUser] = useState(false)
  const [settingsSection, setSettingsSection] = useState('account')
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [prefLang, setPrefLang] = useState('en')
  const [langSaved, setLangSaved] = useState(false)

  const { t, i18n: i18nHook } = useTranslation()

  const firstName = session?.user?.user_metadata?.first_name ||
    session?.user?.user_metadata?.full_name?.split(' ')[0] || session?.user?.email

  const getInitials = (p) => {
    const fn = p?.first_name || session?.user?.user_metadata?.first_name || ''
    const ln = p?.last_name || session?.user?.user_metadata?.last_name || ''
    const first = fn.trim().charAt(0).toUpperCase()
    const last = ln.trim().charAt(0).toUpperCase()
    if (first && last) return first + last
    if (first) return first
    return (session?.user?.email || 'U').charAt(0).toUpperCase()
  }
  const initials = getInitials(userProfile)

  // ── Tour engine wiring (v1 Welcome tour) ──────────────────────────────
  const { actions: tourActions } = useTour()
  const tourFiredRef = useRef(false)

  // Auto-fire the Welcome tour once the user is actually ON the dashboard:
  // init() must have finished (profileChecked) AND the HomeSetup wizard must
  // not be showing (showSetup). We deliberately do NOT gate on userProfile —
  // it can be null on the immediate post-signup path (the profiles row isn't
  // readable yet), which previously blocked the tour forever and only resolved
  // on a reload. shouldFireOnSignup is the DB gate (false once completed/dismissed).
  useEffect(() => {
    const uid = session?.user?.id
    if (!uid || !profileChecked || showSetup || tourFiredRef.current) return
    let cancelled = false
    shouldFireOnSignup(uid).then(go => {
      if (cancelled || tourFiredRef.current || !go) return
      tourFiredRef.current = true
      tourActions.startTour('welcome', null)
    })
    return () => { cancelled = true }
  }, [session, profileChecked, showSetup, tourActions])

  // Tour bubbles ask the dashboard to open flows via the app event bus
  // (no prop-drilling — see src/lib/appEvents.js).
  useEffect(() => {
    const off = onTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, ({ action }) => {
      if (action === TOUR_ACTIONS.OPEN_APPLIANCE_ADD) { setActiveTab('home_profile'); setShowAdd(true) }
      else if (action === TOUR_ACTIONS.OPEN_SHEET_ACTIONS) { setShowEmergencyWizard(true) }
      else if (action === TOUR_ACTIONS.FOCUS_EMERGENCY) { setActiveTab('home'); setShowEmergencyContacts(true) }
      else if (action === TOUR_ACTIONS.CLOSE_APPLIANCE_ADD) { setShowAdd(false) }
      else if (action === TOUR_ACTIONS.CLOSE_EMERGENCY_CONTACTS) { setShowEmergencyContacts(false) }
      else if (action === TOUR_ACTIONS.CLOSE_SHEET_WIZARD) { setShowEmergencyWizard(false) }
    })
    return off
  }, [])

  useEffect(() => {
    const handleResize = () => setIsNarrow(window.innerWidth < 720)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(e.target))
        setPropertyDropdownOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchProperties = async () => {
    const { data } = await supabase
      .from('properties').select('*').eq('user_id', session?.user?.id)
      .order('is_primary', { ascending: false }).order('created_at', { ascending: true })
    const props = data || []
    setProperties(props)
    return props
  }

  useEffect(() => {
    const init = async () => {
      try {
        const [props, { data: profileData }, { data: betaUser }] = await Promise.all([
          fetchProperties(),
          supabase.from('profiles').select('first_name,last_name,dismissed_emergency_promo,completed_emergency_setup,language_preference').eq('id', session?.user?.id).maybeSingle(),
          supabase.from('beta_users').select('id').eq('email', session.user.email).maybeSingle(),
        ])
        if (profileData) {
          setUserProfile(profileData)
          if (profileData.language_preference) setPrefLang(profileData.language_preference)
        }
        setIsBetaUser(!!betaUser)
        const primary = props.find(p => p.is_primary) || props[0]
        const isComplete = primary && primary.address && primary.address.trim() !== ''
        if (!isComplete) {
          setProfileIncomplete(true)
          setShowSetup(true)
        } else {
          const { data: memberships } = await supabase
            .from('home_members')
            .select('home_id, homes(id, address, city, state, zip, display_address, display_city, display_state, display_zip)')
            .eq('user_id', session?.user?.id)
          const homesList = (memberships || []).map(m => m.homes).filter(Boolean)
          const firstHome = homesList[0] ?? null
          setHomes(homesList)
          setActiveHomeId(firstHome?.id ?? null)
          setActivePropertyId(primary?.id || null)
          setProfile(primary || {})
        }
      } catch (err) {
        console.error('Casavio init error:', err)
        setShowSetup(true)
      } finally {
        setProfileChecked(true)
      }
    }
    init()
  }, [session?.user?.id])

  // Promo detection runs after init() — separate effect so it never blocks setProfileChecked(true)
  useEffect(() => {
    if (!activeHomeId || !userProfile) return
    if (userProfile.dismissed_emergency_promo || userProfile.completed_emergency_setup) return
    let cancelled = false
    const checkPromo = async () => {
      const [{ count: memberCount }, { count: contactCount }] = await Promise.all([
        supabase.from('household_members').select('id', { count: 'exact', head: true }).eq('home_id', activeHomeId),
        supabase.from('emergency_contacts').select('id', { count: 'exact', head: true }).eq('home_id', activeHomeId),
      ])
      if (!cancelled && (memberCount ?? 0) === 0 && (contactCount ?? 0) === 0) {
        setShowEmergencyPromo(true)
      }
    }
    checkPromo()
    return () => { cancelled = true }
  }, [activeHomeId, userProfile])

  const handleDismissEmergencyPromo = async () => {
    setShowEmergencyPromo(false)
    await supabase.from('profiles').update({ dismissed_emergency_promo: true }).eq('id', session?.user?.id)
  }

  const handleEmergencySetupComplete = async () => {
    setShowEmergencyWizard(false)
    setShowEmergencyPromo(false)
    await supabase.from('profiles').update({ completed_emergency_setup: true }).eq('id', session?.user?.id)
  }

  const fetchAppliances = async (hid) => {
    const homeId = hid !== undefined ? hid : activeHomeId
    if (!homeId) { setAppliances([]); setLoading(false); return }
    const list = await fetchAppliancesForHome(supabase, homeId)
    setAppliances(list); setLoading(false)
  }
  const fetchSystems = async (pid) => {
    const propertyId = pid ?? activePropertyId
    if (!propertyId) return
    const { data } = await supabase.from('systems').select('*').eq('property_id', propertyId).order('created_at', { ascending: false })
    setSystems(data || []); setSystemsLoading(false)
  }
  const fetchServices = async (pid) => {
    const propertyId = pid ?? activePropertyId
    if (!propertyId) return
    const { data } = await supabase.from('services').select('*').eq('property_id', propertyId).order('created_at', { ascending: false })
    setServices(data || []); setServicesLoading(false)
  }
  const fetchMaintenanceLogs = async (pid) => {
    const propertyId = pid ?? activePropertyId
    if (!propertyId) return
    const { data } = await supabase.from('maintenance_logs').select('*')
      .eq('property_id', propertyId).order('completed_date', { ascending: false }).limit(100)
    setMaintenanceLogs(data || []); setMaintenanceLogsLoading(false)
  }

  const fetchDueParts = async (pid) => {
    const propertyId = pid ?? activePropertyId
    if (!propertyId) return
    const today = new Date()
    const in30 = new Date(today)
    in30.setDate(in30.getDate() + 30)
    const { data } = await supabase
      .from('appliance_parts')
      .select(`
        id, next_due_date, interval_days, last_ordered_at, status,
        custom_name, custom_asin, custom_url, custom_price,
        asset_id, system_id,
        parts_catalog (
          id, name, description, category, amazon_asin, amazon_url, unit_price, image_url
        )
      `)
      .eq('property_id', propertyId)
      .eq('status', 'active')
      .lte('next_due_date', in30.toISOString().split('T')[0])
      .order('next_due_date', { ascending: true })
    setDueParts(data || [])
    setDuePartsLoading(false)
  }

  const fetchServiceLogs = async (pid) => {
    const propertyId = pid ?? activePropertyId
    if (!propertyId) return
    const { data } = await supabase.from('service_logs').select('*').eq('property_id', propertyId)
    setServiceLogs(data || [])
  }

  const fetchHomeProfile = async (hid) => {
    const homeId = hid ?? activeHomeId
    if (!homeId) return
    const { data } = await supabase.from('homes').select('*').eq('id', homeId).single()
    setHomeProfile(data || null)
  }

  const fetchRooms = async (hid) => {
    const homeId = hid ?? activeHomeId
    if (!homeId) return
    const { data } = await supabase.from('rooms').select('*').eq('home_id', homeId).order('created_at')
    setRooms(data || [])
  }

  const fetchJobs = async (hid) => {
    const homeId = hid ?? activeHomeId
    if (!homeId) return
    const { data } = await supabase.from('jobs').select('*').eq('home_id', homeId).order('created_at', { ascending: false }).limit(200)
    const allJobs = data || []
    setJobs(allJobs)
    setJobsLoading(false)
    setWishlistItems(allJobs.filter(j => j.urgency === 'wishlist' && j.status !== 'complete'))
  }

  const fetchFireChecklist = async (homeId) => {
    if (!homeId) return
    const { data } = await supabase.from('safety_checklists').select('*').eq('home_id', homeId).eq('checklist_type', 'fire_safety').order('created_at', { ascending: false }).limit(1).maybeSingle()
    setFireChecklist(data)
    setFireCompletedItems(data?.completed_items || [])
  }

  const saveFireChecklist = async () => {
    if (!activeHomeId) return
    setFireSaving(true)
    const now = new Date().toISOString()
    if (fireChecklist) {
      await supabase.from('safety_checklists').update({ completed_items: fireCompletedItems, completed_at: now, completed_by: session.user.id }).eq('id', fireChecklist.id)
    } else {
      const { data } = await supabase.from('safety_checklists').insert({ home_id: activeHomeId, checklist_type: 'fire_safety', completed_items: fireCompletedItems, completed_at: now, completed_by: session.user.id }).select().single()
      setFireChecklist(data)
    }
    setFireSaving(false)
    setShowFireSafety(false)
  }

  const addToWishlist = async (suggestion) => {
    if (!activeHomeId) return
    const { error } = await supabase.from('jobs').insert({
      home_id: activeHomeId,
      created_by: session.user.id,
      urgency: 'wishlist',
      category: suggestion.category,
      title: suggestion.jobTitle,
      notes: suggestion.desc,
      priority: 'medium',
      est_cost_low: suggestion.estLow || null,
      est_cost_high: suggestion.estHigh || null,
      status: 'new',
      source: suggestion.source || 'energy_score',
    })
    if (!error) {
      fetchJobs(activeHomeId)
      setWishlistToast(suggestion.label || suggestion.jobTitle)
      setTimeout(() => setWishlistToast(null), 3000)
    }
  }

  const saveJob = async () => {
    if (!jobData.title) { alert('Please add a title'); return }
    const { data, error } = await supabase.from('jobs').insert({
      home_id: activeHomeId,
      created_by: session.user.id,
      urgency: jobData.urgency,
      category: jobData.category || null,
      title: jobData.title,
      priority: jobData.priority || 'medium',
      est_cost_low: jobData.est_cost_low ? parseFloat(jobData.est_cost_low) : null,
      est_cost_high: jobData.est_cost_high ? parseFloat(jobData.est_cost_high) : null,
      preferred_date: jobData.preferred_date || null,
      notes: jobData.notes || null,
      status: 'new',
      source: 'owner',
    }).select().single()
    if (error) { alert('Failed to save: ' + error.message); return }
    await supabase.from('job_history').insert({ job_id: data.id, status: 'new', note: `Job created`, changed_by: session.user.id })
    setShowAddJob(false)
    setJobData({ urgency: 'wishlist', priority: 'medium' })
    setJobStep(1)
    fetchJobs()
  }

  const markJobComplete = async (id) => {
    await supabase.from('jobs').update({ status: 'complete', completed_date: new Date().toISOString().split('T')[0] }).eq('id', id)
    fetchJobs()
  }

  const updateJobStatus = async (id, status) => {
    const update = { status }
    if (status === 'complete') update.completed_date = new Date().toISOString().split('T')[0]
    await supabase.from('jobs').update(update).eq('id', id)
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...update } : j))
    if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, ...update }))
  }

  const deleteJob = async (id) => {
    await supabase.from('jobs').delete().eq('id', id)
    setJobs(prev => prev.filter(j => j.id !== id))
    setWishlistItems(prev => prev.filter(j => j.id !== id))
    setSelectedJob(null)
  }

  const saveExterior = async () => {
    if (!activeHomeId) return
    setExteriorSaving(true)
    const fields = ['exterior_paint_brand','exterior_paint_color','exterior_paint_code','exterior_paint_finish',
      'exterior_trim_brand','exterior_trim_color','exterior_trim_code','exterior_trim_finish',
      'roof_material','roof_color','roof_year_installed','driveway_material','fence_material',
      'fence_color','garage_door_color','front_door_color','exterior_notes']
    const update = {}
    for (const field of fields) update[field] = exteriorForm[field] || null
    await supabase.from('homes').update(update).eq('id', activeHomeId)
    setHomeProfile(prev => ({ ...prev, ...exteriorForm }))
    setEditingExterior(false)
    setExteriorSaving(false)
  }

  const saveInteriorNotes = async (notes) => {
    if (!activeHomeId) return
    await supabase.from('homes').update({ interior_notes: notes }).eq('id', activeHomeId)
    setHomeProfile(prev => ({ ...prev, interior_notes: notes }))
  }

  useEffect(() => {
    if (activeHomeId || activePropertyId) {
      fetchAppliances()
      fetchSystems()
      fetchServices()
      fetchMaintenanceLogs()
      fetchDueParts()
      fetchServiceLogs()
      fetchHomeProfile()
      fetchRooms()
      fetchJobs()
      fetchFireChecklist(activeHomeId)
    }
  }, [activePropertyId, activeHomeId])

  const switchProperty = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId)
    if (!prop) return
    setActivePropertyId(propertyId)
    setProfile(prop)
    setPropertyDropdownOpen(false)
    setLoading(true); setSystemsLoading(true); setServicesLoading(true); setMaintenanceLogsLoading(true)
    fetchSystems(propertyId); fetchServices(propertyId); fetchMaintenanceLogs(propertyId)
  }

  const switchHome = (homeId) => {
    const home = homes.find(h => h.id === homeId)
    if (!home) return
    setActiveHomeId(homeId)
    setPropertyDropdownOpen(false)
    setLoading(true)
  }

  const handleSetupComplete = async (propertyId) => {
    try {
      const [props, { data: memberships }] = await Promise.all([
        fetchProperties(),
        supabase.from('home_members').select('home_id, homes(id, address, city, state, zip, display_address, display_city, display_state, display_zip)').eq('user_id', session?.user?.id),
      ])
      const homesList = (memberships || []).map(m => m.homes).filter(Boolean)
      const firstHome = homesList[0] ?? null
      setHomes(homesList)
      setActiveHomeId(firstHome?.id ?? null)
      const prop = props.find(p => p.id === propertyId) || props.find(p => p.is_primary) || props[0]
      if (prop) {
        setActivePropertyId(prop.id)
        setProfile(prop)
        fetchSystems(prop.id)
        fetchServices(prop.id)
        fetchMaintenanceLogs(prop.id)
      } else {
        setLoading(false)
        setSystemsLoading(false)
        setServicesLoading(false)
        setMaintenanceLogsLoading(false)
      }
    } catch (err) {
      console.error('handleSetupComplete error:', err)
      setLoading(false)
      setSystemsLoading(false)
      setServicesLoading(false)
      setMaintenanceLogsLoading(false)
    } finally {
      setShowSetup(false)
      setShowAddProperty(false)
      setProfileIncomplete(false)
      setProfileChecked(true)
    }
  }
  const handleProfileUpdated = async () => {
    const props = await fetchProperties()
    const prop = props.find(p => p.id === activePropertyId)
    if (prop) setProfile(prop)
  }
  const handleSaved = () => { setShowAdd(false); setAddInitialType(null); fetchAppliances() }

  const dismissStandardSlot = async (slotKey) => {
    if (!activeHomeId) return
    const current = Array.isArray(homeProfile?.dismissed_standard_appliances) ? homeProfile.dismissed_standard_appliances : []
    if (current.includes(slotKey)) return
    const next = [...current, slotKey]
    setHomeProfile(prev => prev ? { ...prev, dismissed_standard_appliances: next } : prev) // optimistic
    await supabase.from('homes').update({ dismissed_standard_appliances: next }).eq('id', activeHomeId)
  }
  const handleSystemSaved = () => { setShowAddSystem(false); fetchSystems() }
  const handleServiceSaved = () => { setShowAddService(false); fetchServices() }
  const handleSignOut = async () => { await supabase.auth.signOut() }

  useEffect(() => {
    if (profile && activeHomeId && homes.length > 0) {
      const home = homes.find(h => h.id === activeHomeId)
      if (home && !home.onboarding_complete && !home.address) {
        setShowOnboarding(true)
      }
    }
  }, [profile, activeHomeId, homes])

  useEffect(() => {
    const loadRelation = async () => {
      if (!session?.user?.id || !activeHomeId) return
      const { data } = await supabase
        .from('home_members')
        .select('relation')
        .eq('user_id', session.user.id)
        .eq('home_id', activeHomeId)
        .single()
      if (data?.relation) setUserRelation(data.relation)
    }
    loadRelation()
  }, [session?.user?.id, activeHomeId])

  const updateRelation = async (rel) => {
    setUserRelation(rel)
    if (!session?.user?.id || !activeHomeId) return
    await supabase
      .from('home_members')
      .update({ relation: rel })
      .eq('user_id', session.user.id)
      .eq('home_id', activeHomeId)
  }

  const saveHomeProfile = async () => {
    if (!activeHomeId) return
    setProfileSaving(true)
    const diffDisplay = (typed, canonical) => {
      const d = (typed || '').trim()
      const c = (canonical || '').trim()
      return d.toLowerCase() !== c.toLowerCase() ? (d || null) : null
    }
    const { error } = await supabase.from('homes').update({
      // Canonical address — persisted on Save only. The autocomplete onSelect no
      // longer writes to the DB directly; it stages into homeProfileForm, and this
      // explicit Save is the single write path for the whole address.
      address: homeProfileForm.address ?? null,
      city: homeProfileForm.city ?? null,
      state: homeProfileForm.state ?? null,
      zip: homeProfileForm.zip ?? null,
      display_address: diffDisplay(homeProfileForm.addr_street, homeProfileForm.address),
      display_city:    diffDisplay(homeProfileForm.addr_city,   homeProfileForm.city),
      display_state:   diffDisplay(homeProfileForm.addr_state,  homeProfileForm.state),
      display_zip:     diffDisplay(homeProfileForm.addr_zip,    homeProfileForm.zip),
      property_type: homeProfileForm.property_type || null,
      year_built: homeProfileForm.year_built || null,
      bedrooms: homeProfileForm.bedrooms || null,
      bathrooms: homeProfileForm.bathrooms || null,
      sq_footage: homeProfileForm.sq_footage || null,
      lot_size: homeProfileForm.lot_size || null,
      hoa: homeProfileForm.hoa || false,
      hoa_name: homeProfileForm.hoa ? (homeProfileForm.hoa_name || null) : null,
      hoa_fee: homeProfileForm.hoa && homeProfileForm.hoa_fee ? parseFloat(homeProfileForm.hoa_fee) : null,
      latitude: homeProfileForm.latitude ?? null,
      longitude: homeProfileForm.longitude ?? null,
      google_place_id: homeProfileForm.google_place_id ?? null,
    }).eq('id', activeHomeId)
    setProfileSaving(false)
    if (!error) {
      setHomeProfile(prev => ({ ...(prev || {}), ...homeProfileForm }))
      setProfileSnapshot(homeProfileForm) // re-baseline: saved form IS the new clean state, so a later X-close won't false-positive as dirty
      setEditingProfile(false)
      setProfileSaved(true)
      setTimeout(() => setProfileSaved(false), 2000)
      supabase.functions.invoke('lookup-emergency-services', { body: { home_id: activeHomeId } }).catch(() => {})
    }
  }

  // Open the Edit Home Profile form and snapshot the initial form state so the
  // X-close can detect unsaved changes (dirty tracking).
  const openProfileEditForm = () => {
    const h = homeProfile || {}
    const init = {
      ...h,
      addr_street: h.display_address || h.address || '',
      addr_city: h.display_city || h.city || '',
      addr_state: h.display_state || h.state || '',
      addr_zip: h.display_zip || h.zip || '',
    }
    setHomeProfileForm(init)
    setProfileSnapshot(init)
    setEditingProfile(true)
  }
  // Close the Edit form. If the form is dirty (changed since open), confirm
  // before discarding — nothing has been written to the DB yet (Save-only).
  const closeProfileEditForm = () => {
    const dirty = profileSnapshot && JSON.stringify(homeProfileForm) !== JSON.stringify(profileSnapshot)
    if (!dirty || window.confirm('Discard unsaved changes?')) setEditingProfile(false)
  }

  const runCountyEnrich = async () => {
    setCountyEnrichLoading(true)
    setCountyEnrichBanner(null)
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      const resp = await fetch('https://maajaepibcnbxcqjahej.supabase.co/functions/v1/enrich-home-from-county', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession.access_token}` },
        body: JSON.stringify({ home_id: activeHomeId, mode: 'apply' }),
      })
      const result = await resp.json()
      if (result.status === 'success' || result.status === 'partial') {
        const fieldsCount = Object.keys(result.pulled || {}).length
        const systemsCount = (result.systems_created || []).length
        setCountyEnrichBanner({ type: 'success', message: `Found your home in ${result.county_detected}! Pulled ${fieldsCount} field${fieldsCount !== 1 ? 's' : ''}${systemsCount > 0 ? ` and added ${systemsCount} home system${systemsCount !== 1 ? 's' : ''}` : ''}.` })
        await fetchHomeProfile(activeHomeId)
        fetchSystems()
      } else if (result.status === 'unsupported_county') {
        setCountyEnrichBanner({ type: 'warning', message: "Your county isn't supported yet, but we're working on it. We've logged your address so we know to add it next." })
      } else {
        setCountyEnrichBanner({ type: 'error', message: "We couldn't find your home in county records. You can still enter details manually below — and we've let the team know so we can investigate." })
      }
    } catch {
      setCountyEnrichBanner({ type: 'error', message: "We couldn't find your home in county records. You can still enter details manually below — and we've let the team know so we can investigate." })
    } finally {
      setCountyEnrichLoading(false)
    }
  }

  const openCountyPortal = () => {
    const h = homeProfile || {}
    const url = getCountyPortalUrl(h.county_data_source, h.address, h.city, h.state, h.zip)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const submitProblemReport = async () => {
    if (!problemCategory) return
    setProblemSubmitting(true)
    await supabase.from('bug_reports').insert({
      user_id: session.user.id,
      category: problemCategory,
      description: problemDescription.trim() || null,
      status: 'open',
    })
    await supabase.functions.invoke('notify-owner', {
      body: {
        event: 'bug_reported',
        data: {
          category: problemCategory,
          description: problemDescription.trim() || null,
          reported_by: firstName || session.user.email,
        },
      },
    })
    setProblemSubmitting(false)
    setShowProblemReport(false)
    setProblemCategory('')
    setProblemDescription('')
    alert('Thanks for the report! It has been sent to Kenneth.')
  }

  const handleTaskComplete = async ({ taskKey, date, cost, provider, note, didItMyself }) => {
    if (!selectedTask) return
    const { taskObj, applianceName: itemName, itemId, itemType } = selectedTask
    const dateUsed = date || new Date().toISOString().split('T')[0]
    const nextDue = calcMaintenanceNextDue(dateUsed, taskObj.interval)
    const costNum = cost !== null && cost !== undefined ? parseFloat(cost) : null

    let appliance = null
    if (itemType === 'appliance') {
      appliance = appliances.find(a => a.id === itemId)
      if (appliance) {
        const updatedDates = { ...(appliance.task_dates || {}), [taskObj.task]: { lastCompleted: dateUsed, nextDue, interval: taskObj.interval } }
        const updatedCompleted = { ...(appliance.completed_tasks || {}), [taskKey || `${taskObj.task}_${taskObj.interval}`]: { date: dateUsed } }
        const costEntry = costNum !== null ? { task: taskObj.task, date: dateUsed, amount: costNum, provider: provider || null, note: note || null } : null
        const updatedHistory = costEntry ? [costEntry, ...(appliance.cost_history || [])] : (appliance.cost_history || [])
        // Fields live on three different tables now — fan out.
        await Promise.all([
          updateApplianceSlot(supabase, appliance.id, { task_dates: updatedDates }),
          updateApplianceAsset(supabase, appliance.id, { completed_tasks: updatedCompleted }),
          updateAppliancePlacement(supabase, appliance.id, { cost_history: updatedHistory }),
        ])
      }
    } else {
      const system = systems.find(s => s.id === itemId)
      if (system) {
        const updatedDates = { ...(system.task_dates || {}), [taskObj.task]: { lastCompleted: dateUsed, nextDue, interval: taskObj.interval } }
        await supabase.from('systems').update({ task_dates: updatedDates }).eq('id', itemId)
      }
    }
    await supabase.from('maintenance_logs').insert({
      user_id: session?.user?.id, property_id: activePropertyId,
      item_type: itemType,
      // For appliances, item_id (bigint, legacy) is null; identity comes from asset_id+placement_id (uuid).
      // For systems, item_id remains the bigint systems.id.
      item_id: itemType === 'appliance' ? null : itemId,
      asset_id: itemType === 'appliance' ? (appliance?.asset_id || null) : null,
      placement_id: itemType === 'appliance' ? itemId : null,
      item_name: itemName,
      task_name: taskObj.task, completed_date: dateUsed,
      cost: costNum ?? null, provider: provider ?? null, note: note ?? null,
      did_it_myself: didItMyself ?? null,
    })
    setSelectedTask(null)
    fetchAppliances(); fetchSystems(); fetchMaintenanceLogs()
  }

  const handleOrderNow = async (part) => {
    const asin = part.custom_asin || part.parts_catalog?.amazon_asin
    const name = part.custom_name || part.parts_catalog?.name || 'Part'
    const price = part.custom_price ?? part.parts_catalog?.unit_price ?? null

    const url = asin
      ? `https://www.amazon.com/dp/${asin}?tag=${AFFILIATE_TAG}`
      : part.custom_url || part.parts_catalog?.amazon_url

    if (!url) return

    window.open(url, '_blank', 'noopener,noreferrer')

    await supabase.from('parts_orders').insert({
      user_id: session?.user?.id,
      appliance_part_id: part.id,
      part_name: name,
      asin: asin || null,
      order_url: url,
      unit_price: price,
      total_price: price,
      affiliate_tag: AFFILIATE_TAG,
      status: 'ordered',
      ordered_at: new Date().toISOString(),
      order_source: 'manual',
    })

    const nextDue = part.interval_days
      ? new Date(Date.now() + part.interval_days * 86400000).toISOString().split('T')[0]
      : null

    await supabase.from('appliance_parts').update({
      last_ordered_at: new Date().toISOString().split('T')[0],
      ...(nextDue ? { next_due_date: nextDue } : {}),
    }).eq('id', part.id)

    setOrderedParts(prev => ({ ...prev, [part.id]: true }))
    setTimeout(() => fetchDueParts(), 1500)
  }

  const getOrderNow = (item) => {
    if (item.taskType !== 'auto_order') return null
    return () => {
      // item.itemId is the placement uuid; look up its asset_id to match dueParts.
      const appl = appliances.find(a => a.id === item.itemId)
      const assetId = appl?.asset_id
      const match = dueParts.find(p =>
        p.asset_id === assetId &&
        (p.parts_catalog?.name?.toLowerCase().includes(item.task.toLowerCase()) ||
         p.custom_name?.toLowerCase().includes(item.task.toLowerCase()))
      )
      if (match) handleOrderNow(match)
      else {
        const makeModel = appl ? `${appl.make} ${appl.model}` : item.itemName
        const q = encodeURIComponent(`${makeModel} ${item.task}`)
        window.open(`https://www.amazon.com/s?k=${q}&tag=casavio-20`, '_blank', 'noopener,noreferrer')
      }
    }
  }

  const allMaintenanceItems = [
    ...appliances.flatMap(a => {
      const sched = maintenanceSchedules[a.type] || maintenanceSchedules.other || []
      const dates = a.task_dates || {}
      return sched.map(s => {
        // task_dates may be keyed by task name (Dashboard saves) or task_interval (ApplianceDetail saves)
        const td = dates[s.task] || dates[`${s.task}_${s.interval}`]
        const nextDue = td?.nextDue || null
        return { itemId: a.id, itemType: 'appliance', itemName: a.nickname || a.name, task: s.task, interval: s.interval, taskType: s.type, sourceType: a.type, partCost: s.partCost || null, lastCompleted: td?.lastCompleted || null, nextDue, status: getUpcomingStatus(nextDue) }
      })
    }),
    ...systems.flatMap(s => {
      const sched = systemSchedules[s.type] || []
      const dates = s.task_dates || {}
      return sched.map(item => {
        const td = dates[item.task] || dates[`${item.task}_${item.interval}`]
        const nextDue = td?.nextDue || null
        return { itemId: s.id, itemType: 'system', itemName: s.name, task: item.task, interval: item.interval, taskType: item.type, sourceType: s.type, lastCompleted: td?.lastCompleted || null, nextDue, status: getUpcomingStatus(nextDue) }
      })
    }),
  ].sort((a, b) => {
    const ord = { overdue: 0, soon: 1, upcoming: 2, good: 4 }
    const ao = a.status !== null ? (ord[a.status] ?? 3) : 3
    const bo = b.status !== null ? (ord[b.status] ?? 3) : 3
    if (ao !== bo) return ao - bo
    if (!a.nextDue && !b.nextDue) return 0
    if (!a.nextDue) return 1; if (!b.nextDue) return -1
    return new Date(a.nextDue) - new Date(b.nextDue)
  })
  const needsAttentionItems = allMaintenanceItems.filter(t => t.status === 'overdue' || t.status === 'soon')
  const overdueCount = needsAttentionItems.filter(t => t.status === 'overdue').length
  const upcomingItems = allMaintenanceItems.filter(t => t.status === 'upcoming')
  const neverStartedItems = allMaintenanceItems.filter(t => {
    if (t.lastCompleted || t.nextDue) return false
    const i = (t.interval || '').toLowerCase()
    return !i.includes('every use') && !i.includes('each use') && !i.includes('as needed') && !i.includes('as released')
  })

  const allScheduleItems = appliances.flatMap(a =>
    (maintenanceSchedules[a.type] || maintenanceSchedules.other).map(s => ({
      ...s, applianceName: a.nickname || a.name, applianceMake: a.make, applianceType: a.type, applianceId: a.id,
    }))
  )

  const diyItems = allScheduleItems.filter(i => i.type === 'diy')
  const contractorItems = allScheduleItems.filter(i => i.type === 'contractor')
  const autoOrderItems = allScheduleItems.filter(i => i.type === 'auto_order')
  const isNewAccount = (appliances?.length || 0) === 0 && (systems?.length || 0) === 0
  const score = calculateScore(profile, appliances, systems)
  const grade = getLetterGrade(score)
  const scoreColor = getScoreColor(score)

  const propertyAddress = resolveDisplayAddress(homeProfile || profile) || null
  const activeHomeData = homes.find(h => h.id === activeHomeId)
  const activeHomeAddress = resolveDisplayAddress(activeHomeData) || null
  const propertyTypeLabel = profile?.property_type
    ? propertyTypeLabels[profile.property_type] || 'Property' : null

  const recurringServices = services.filter(s => s.service_type === 'recurring')
  const contractorJobs = services.filter(s => s.service_type === 'contractor')

  // Home profile completeness
  const hp = homeProfile || {}
  const countyAppliedFields = useMemo(() => {
    const log = homeProfile?.county_enrichment_log
    if (!Array.isArray(log) || log.length === 0) return new Set()
    const latest = log[log.length - 1]
    return new Set(Array.isArray(latest?.applied) ? latest.applied : [])
  }, [homeProfile?.county_enrichment_log])
  const hpFields = ['address', 'city', 'state', 'property_type', 'year_built', 'bedrooms', 'bathrooms', 'sq_footage', 'lot_size', 'exterior_paint_color', 'roof_material', 'front_door_color']
  const roomBonus = Math.min(rooms.filter(r => r.flooring_type || r.wall_paint_color).length, 3)
  const hpFilledCount = hpFields.filter(field => hp[field]).length + roomBonus
  const totalHpFields = hpFields.length + 3
  const hpCompleteness = Math.round((hpFilledCount / totalHpFields) * 100)
  const getHomeDetailsSummary = () => {
    const parts = []
    if (hp.exterior_paint_color) parts.push('Exterior paint')
    if (rooms?.length > 0) parts.push(`${rooms.length} room${rooms.length !== 1 ? 's' : ''}`)
    if (hp.interior_notes) parts.push('Notes')
    return parts.length > 0 ? parts.join(' · ') : 'Tap to add exterior colors, room profiles and more'
  }
  const profLbl = { fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', display: 'block', fontFamily: 'Calibri, sans-serif' }
  const profInp = { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '16px', fontFamily: f, color: C.brown, boxSizing: 'border-box', outline: 'none', WebkitUserSelect: 'text', userSelect: 'text', touchAction: 'manipulation', WebkitAppearance: 'none' }
  const addrHint = (display, canonical) => {
    if (!canonical?.trim()) return null
    const match = (display || '').trim().toLowerCase() === canonical.trim().toLowerCase()
    return <div style={{ fontSize: '11px', fontFamily: 'Calibri, sans-serif', marginBottom: '10px', color: match ? '#2E6B4A' : '#9A7B68', fontStyle: match ? 'normal' : 'italic' }}>{match ? 'Verified ✓ matches' : `Verified: ${canonical.trim()}`}</div>
  }
  const profPtLabels = { single_family: 'Single Family', condo: 'Condo / Townhome', multi_family: 'Multi-Family', mobile: 'Mobile Home', vacation: 'Vacation Home', other: 'Property' }

  // Annual spend + savings across all appliances
  const currentYear = new Date().getFullYear().toString()
  const natAvgMap = { 'Filter Replacement': 25, 'Coil Cleaning': 150, 'Full System Inspection': 120, 'Water Filter Replacement': 45, 'Anode Rod Inspection': 180, 'Vent Duct Cleaning': 130, 'Full Service Inspection': 100, 'Battery Replacement': 8, 'Full Replacement': 35, 'Drum Cleaning Cycle': 12, 'Descaling Treatment': 12, 'Lubricate Moving Parts': 12 }
  const applianceAnnualSpend = appliances.reduce((total, a) => {
    return total + (a.cost_history || []).filter(h => h.date?.startsWith(currentYear)).reduce((sum, h) => sum + (h.amount || 0), 0)
  }, 0)
  const serviceLogCost = serviceLogs.filter(l => l.visited_at?.startsWith(currentYear)).reduce((sum, l) => sum + (l.cost || 0), 0)
  const annualSpend = applianceAnnualSpend + serviceLogCost
  const annualSaved = appliances.reduce((total, a) => {
    return total + (a.cost_history || []).filter(h => h.date?.startsWith(currentYear) && h.amount).reduce((sum, h) => {
      const avg = natAvgMap[h.task]; return avg && h.amount < avg ? sum + (avg - h.amount) : sum
    }, 0)
  }, 0)

  // Energy Score stats — exclude appliance types not rated by ENERGY STAR
  const ENERGY_STAR_EXCLUDED_TYPES = ['oven', 'range', 'microwave', 'range_hood', 'garbage_disposal', 'stove', 'cooktop']
  const eligibleAppliances = appliances.filter(a => !ENERGY_STAR_EXCLUDED_TYPES.includes(a.type))
  const excludedAppliances = appliances.filter(a => ENERGY_STAR_EXCLUDED_TYPES.includes(a.type))
  const energyCertifiedCount = eligibleAppliances.filter(a => a.energy_star === true).length
  const energyTotalCost = Math.round(appliances.reduce((sum, a) => sum + (a.est_annual_energy_cost || 0), 0))
  const energyRatio = eligibleAppliances.length > 0 ? energyCertifiedCount / eligibleAppliances.length : 0
  const smartBonus = (homeProfile?.smart_thermostat ? 0.1 : 0) + (homeProfile?.smart_power_strips ? 0.06 : 0) + (homeProfile?.led_lighting ? 0.1 : 0)
  const energyScore = Math.min(1, energyRatio + smartBonus)
  const energyGrade = eligibleAppliances.length === 0 && !smartBonus ? '—' : energyScore >= 0.8 ? 'A' : energyScore >= 0.6 ? 'B' : energyScore >= 0.4 ? 'C' : energyScore >= 0.2 ? 'D' : 'F'
  const energyGradeColor = energyGrade === 'A' ? C.success : energyGrade === 'B' ? '#4A8A6A' : energyGrade === 'C' ? C.amber : energyGrade === '—' ? C.muted : C.danger

  if (!profileChecked) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: C.muted, fontFamily: f, fontStyle: 'italic' }}>Loading...</div>
      </div>
    )
  }
  if (showSetup) return <HomeSetup session={session} onComplete={handleSetupComplete} />

  return (
    <div style={S.container}>

      {/* Header */}
      <div style={S.header} className="casavio-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CasavioLogo size={20} color={C.amber} />
          <span style={{
            fontSize: '10px',
            fontFamily: 'Calibri, sans-serif',
            color: '#9A7B68',
            marginLeft: '8px',
            letterSpacing: '0.5px',
            fontWeight: 'normal',
            verticalAlign: 'middle',
          }}>{VERSION_LABEL}</span>
        </div>
        <div style={S.headerRight}>
          <div style={{ position: 'relative' }}>
            <div
              data-testid="btn-avatar"
              data-tour-anchor="avatar.help_menu"
              style={S.avatar}
              onClick={() => setShowAccountMenu(v => !v)}
              title="Account & Settings"
            >
              {initials}
            </div>
            {showAccountMenu && (
              <>
                <div
                  onClick={() => setShowAccountMenu(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 98 }}
                />
                <div style={{
                  position: 'fixed', top: 56, right: 12,
                  background: C.card, borderRadius: 12,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 8px 32px rgba(44,29,20,0.15)',
                  zIndex: 200, minWidth: 240, overflow: 'hidden',
                }}>
                  <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid #D4C5B5` }}>
                    <div style={{ fontFamily: f, fontSize: 16, color: C.brown }}>
                      {userProfile?.first_name} {userProfile?.last_name}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {activeHomeAddress || '300 Harbor Drive, Novato'}
                    </div>
                    <div style={{ fontSize: 11, color: C.amber, marginTop: 6, fontWeight: 600 }}>
                      Personal Plan · $8/mo
                    </div>
                  </div>
                  {[
                    { emoji: '⚙️', label: 'Settings', section: 'settings' },
                    { emoji: '👤', label: 'My Account', section: 'account' },
                    { emoji: '🏠', label: 'My Role', section: 'role' },
                    { emoji: '🔔', label: 'Notifications', section: 'notifications' },
                    { emoji: '🆘', label: 'Emergency Sheet', section: 'emergency_sheet' },
                  ].map(item => (
                    <div
                      key={item.section}
                      onClick={() => {
                        setShowAccountMenu(false)
                        if (item.section === 'settings') {
                          window.location.href = '/settings'
                        } else if (item.section === 'emergency_sheet') {
                          setShowEmergencyWizard(true)
                        } else {
                          setSettingsSection(item.section)
                          setShowSettings(true)
                        }
                      }}
                      style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      <span style={{ fontSize: 16 }}>{item.emoji}</span>
                      <span style={{ fontSize: 14, color: item.highlight ? C.amber : C.brown }}>{item.label}</span>
                    </div>
                  ))}
                  {/* Reprint button — generates PDF directly without re-walking the wizard */}
                  <div
                    onClick={async () => {
                      try {
                        const { data, error } = await supabase.functions.invoke('generate-emergency-pdf', {
                          body: { home_id: activeHomeId },
                        })
                        if (error || !data?.html) {
                          alert('Could not generate the Emergency Sheet. Please try again.')
                          return
                        }
                        const win = window.open('', '_blank')
                        if (win) {
                          win.document.write(data.html)
                          win.document.close()
                          setTimeout(() => win.print(), 500)
                        }
                      } catch {
                        alert('Could not generate the Emergency Sheet. Please try again.')
                      }
                      setShowAccountMenu(false)
                    }}
                    style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: 16 }}>🖨️</span>
                    <span style={{ fontSize: 14, color: C.brown, fontFamily: 'Calibri, sans-serif' }}>Print Emergency Sheet</span>
                  </div>
                  <div
                    onClick={() => { setShowAccountMenu(false); setShowTourLauncher(true) }}
                    style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <span style={{ fontSize: 16 }}>🧭</span>
                    <span style={{ fontSize: 14, color: C.brown }}>Help &amp; Tours</span>
                  </div>
                  <div
                    onClick={() => { setShowAccountMenu(false); setSettingsSection('plan'); setShowSettings(true) }}
                    style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <span style={{ fontSize: 16 }}>💳</span>
                    <span style={{ fontSize: 14, color: C.amber }}>My Plan</span>
                  </div>
                  <div style={{ height: 1, background: '#E8DDD0' }} />
                  {isBetaUser && (
                    <div
                      onClick={() => { setShowAccountMenu(false); window.open('/beta', '_self') }}
                      style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      <span style={{ fontSize: 16 }}>💬</span>
                      <span style={{ fontSize: 14, color: C.brown }}>Beta Community</span>
                    </div>
                  )}
                  {profile?.id === '26680bcf-f67b-490e-ba08-e1f25ddcfd01' && (
                    <div
                      onClick={() => { setShowAccountMenu(false); window.open('/owner', '_self') }}
                      style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                      <span style={{ fontSize: 16 }}>⚙️</span>
                      <span style={{ fontSize: 14, color: C.brown }}>Owner Dashboard</span>
                    </div>
                  )}
                  <div
                    onClick={() => { setShowAccountMenu(false); handleSignOut() }}
                    style={{ padding: '13px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <span style={{ fontSize: 16 }}>👋</span>
                    <span style={{ fontSize: 14, color: C.muted }}>Sign Out</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Property bar */}
      <div style={S.propertyBar}>
        <div style={S.propertyBarInner}>
          <div ref={propertyDropdownRef} style={{ position: 'relative', flex: 1 }}>
            <div style={S.propertySelector} onClick={() => setPropertyDropdownOpen(!propertyDropdownOpen)}>
              <House size={16} strokeWidth={1.5} color={C.amber} />
              {propertyAddress
                ? <span style={S.propertyAddress}>{propertyAddress}</span>
                : <span style={S.propertyAddressEmpty} onClick={() => setShowSetup(true)}>Set up your home profile</span>}
              <span style={S.propertyChevron}>{propertyDropdownOpen ? '▲' : '▼'}</span>
            </div>
            {propertyDropdownOpen && (
              <div style={S.propertyDropdown}>
                {(homes.length > 0 ? homes : properties).map(item => {
                  const isHome = homes.length > 0
                  const addr = resolveDisplayAddress(item) || 'No address set'
                  const isActive = isHome ? item.id === activeHomeId : item.id === activePropertyId
                  return (
                    <div key={item.id} style={S.propertyDropdownItem(isActive)} onClick={() => isHome ? switchHome(item.id) : switchProperty(item.id)}>
                      <div style={S.propertyDropdownItemName}>
                        {item.nickname || propertyTypeLabels[item.property_type] || (item.address ? item.address.split(' ').slice(-1)[0] + ' St' : 'My Home')}
                        {isActive && ' ✓'}
                      </div>
                      <div style={S.propertyDropdownItemAddress}>{addr}</div>
                    </div>
                  )
                })}
                <div
                  style={{ ...S.propertyDropdownAdd, cursor: 'pointer', opacity: 1 }}
                  onClick={() => { setPropertyDropdownOpen(false); setShowAddProperty(true) }}
                >
                  <span>＋</span>
                  <div style={S.propertyDropdownAddText}>Add Another Property</div>
                </div>
              </div>
            )}
          </div>
          {propertyTypeLabel && <div style={S.propertyType}>{propertyTypeLabel}</div>}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowLangMenu(m => !m)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', color: C.muted, fontFamily: 'Calibri, sans-serif', display: 'flex', alignItems: 'center', gap: '3px' }}
            >
              🌐 {i18nHook.language === 'es' ? 'ES' : 'EN'}
            </button>
            {showLangMenu && (
              <div style={{ position: 'absolute', right: 0, top: '100%', background: '#FDFAF6', border: `1px solid #DDD4C6`, borderRadius: '8px', boxShadow: '0 4px 16px rgba(44,29,20,0.12)', zIndex: 100, minWidth: '110px', overflow: 'hidden' }}>
                <div
                  onClick={() => { i18n.changeLanguage('en'); localStorage.setItem('casavio_preferred_language', 'en'); setShowLangMenu(false) }}
                  style={{ padding: '10px 16px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: '#2C1D14', cursor: 'pointer', fontWeight: i18nHook.language !== 'es' ? 700 : 400 }}
                >
                  English
                </div>
                <div
                  onClick={() => { i18n.changeLanguage('es'); localStorage.setItem('casavio_preferred_language', 'es'); setShowLangMenu(false) }}
                  style={{ padding: '10px 16px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: '#2C1D14', cursor: 'pointer', fontWeight: i18nHook.language === 'es' ? 700 : 400 }}
                >
                  Español
                </div>
                <div style={{ padding: '8px 12px', fontSize: '11px', fontStyle: 'italic', color: C.muted, borderTop: `1px solid ${C.border}`, fontFamily: 'Calibri, sans-serif' }}>
                  {t('language.fullTranslationHint')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        <div style={S.tabBarInner}>
          {tabs.map(tab => {
            const TabIcon = tab.Icon
            return (
              <div key={tab.id} data-testid={`tab-${tab.id}`} data-tour-anchor={`dashboard.${tab.id}_tab`} style={S.tab(activeTab === tab.id, tab.enabled)} onClick={() => tab.enabled && setActiveTab(tab.id)}>
                <div style={S.tabIcon}>
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <TabIcon size={16} strokeWidth={1.5} color={activeTab === tab.id ? C.amber : C.muted} />
                    {tab.id === 'maintenance' && overdueCount > 0 && (
                      <div style={{ position: 'absolute', top: '-4px', right: '-6px', background: C.danger, color: '#fff', borderRadius: '50%', width: '14px', height: '14px', fontSize: '9px', fontFamily: 'Calibri, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>
                        {overdueCount > 9 ? '9+' : overdueCount}
                      </div>
                    )}
                  </div>
                </div>
                <div style={S.tabLabel(activeTab === tab.id)}>{tab.label}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ════════ HOME TAB ════════ */}
      {activeTab === 'home' && (
        <div style={S.content} className="casavio-content">
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '26px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>
              {getGreeting()}, {firstName}
            </div>
            <div style={{ fontSize: '14px', color: C.muted }}>
              {getSmartSubtitle(appliances, systems, allScheduleItems)}
            </div>
          </div>

          {profileIncomplete && (
            <div style={S.setupBanner}>
              <div style={S.setupBannerText}><House size={14} strokeWidth={1.5} color={C.amber} style={{ verticalAlign: 'middle', marginRight: '4px' }} /><strong>Complete your home profile</strong> — add your address and property details.</div>
              <button style={S.setupBannerBtn} onClick={() => setShowSetup(true)}>Set Up Now</button>
            </div>
          )}

          {showEmergencyPromo && (
            <div style={{
              background: 'linear-gradient(135deg, #1A3A2A 0%, #2E5C40 100%)',
              borderRadius: 16, padding: '20px 24px', marginBottom: 20,
              boxShadow: '0 4px 24px rgba(26,58,42,0.18)',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 80, opacity: 0.08, lineHeight: 1 }}>🆘</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 20 }}>🆘</span>
                    <div style={{ fontSize: 11, color: '#7FD4A0', fontFamily: 'Calibri, sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700 }}>
                      5-minute setup
                    </div>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: f, marginBottom: 6 }}>
                    Create your Emergency Sheet
                  </div>
                  <div style={{ fontSize: 13, color: '#A8D8BE', fontFamily: 'Calibri, sans-serif', marginBottom: 18, lineHeight: 1.5 }}>
                    Who lives here, who to call, where the shutoffs are — all in one printable page for first responders.
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setShowEmergencyWizard(true)}
                      style={{
                        background: '#fff', border: 'none', borderRadius: 10,
                        padding: '11px 22px', fontSize: 14, fontFamily: f, fontWeight: 700,
                        color: '#1A3A2A', cursor: 'pointer',
                      }}
                    >
                      Set it up now
                    </button>
                    <button
                      onClick={handleDismissEmergencyPromo}
                      style={{
                        background: 'none', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10,
                        padding: '11px 18px', fontSize: 13, fontFamily: 'Calibri, sans-serif',
                        color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
                      }}
                    >
                      Maybe later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Score + stats */}
          <div style={{ display: isNarrow ? 'block' : 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', marginBottom: '28px' }}>

            {/* Score circle — clickable */}
            <div
              style={{
                background: C.card, borderRadius: '16px', border: `1px solid ${C.border}`,
                boxShadow: '0 2px 12px rgba(44,29,20,0.06)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '28px 20px',
                marginBottom: isNarrow ? '16px' : 0,
                cursor: isNewAccount ? 'default' : 'pointer', transition: 'box-shadow 0.2s',
              }}
              onClick={isNewAccount ? undefined : () => setShowScoreModal(true)}
              title={isNewAccount ? undefined : 'Tap to see score breakdown'}
            >
              <div style={{ position: 'relative', width: '120px', height: '120px', marginBottom: '12px' }}>
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke={C.border} strokeWidth="10" />
                  <circle cx="60" cy="60" r="52" fill="none"
                    stroke={isNewAccount ? C.border : scoreColor.primary} strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={isNewAccount ? '0 327' : `${(score / 100) * 327} 327`}
                    transform="rotate(-90 60 60)" style={{ transition: 'stroke-dasharray 1s ease' }} />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {isNewAccount ? (
                    <div style={{ fontSize: '36px', fontWeight: '700', color: C.muted, fontFamily: f, lineHeight: 1 }}>{t('dashboard.scoreDeferredValue')}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: scoreColor.primary, fontFamily: f, lineHeight: 1 }}>{score}</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: scoreColor.primary, fontFamily: f, marginTop: '2px' }}>{grade}</div>
                    </>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: C.brown, textAlign: 'center' }}>Home Health Score</div>
              <div style={{ fontSize: '10px', color: isNewAccount ? C.muted : C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>
                {isNewAccount ? t('dashboard.scoreDeferredHint') : 'Tap for breakdown →'}
              </div>
            </div>

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr 1fr' : '1fr 1fr 1fr', gap: '12px' }}>
              {/* 1. Open Jobs */}
              {(() => {
                const openJobsCount = jobs.filter(j => j.status !== 'complete').length
                const emergencyCount = jobs.filter(j => j.urgency === 'emergency' && j.status !== 'complete').length
                return (
                  <div
                    onClick={() => { setActiveTab('services'); setJobFilter('all') }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FBF3E8' }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.card }}
                    style={{ background: C.card, borderRadius: '12px', border: emergencyCount > 0 ? '1px solid #DC2626' : `1px solid ${C.border}`, padding: '16px', boxShadow: '0 2px 8px rgba(44,29,20,0.04)', cursor: 'pointer', transition: 'background 0.15s', position: 'relative' }}
                  >
                    <div style={{ marginBottom: '6px', fontSize: '20px', lineHeight: 1 }}>🔨</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: C.brown, fontFamily: f }}>{openJobsCount}</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif', marginTop: '1px' }}>Open Jobs</div>
                    <div style={{ fontSize: '10px', color: emergencyCount > 0 ? '#DC2626' : C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>
                      {openJobsCount === 0 ? 'No open jobs' : emergencyCount > 0 ? `⚡ ${emergencyCount} urgent` : `${openJobsCount} job${openJobsCount !== 1 ? 's' : ''} open`}
                    </div>
                    <div style={{ position: 'absolute', bottom: '10px', right: '12px', fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>→</div>
                  </div>
                )
              })()}
              {/* 2. Wishlist */}
              {(() => {
                const wlCount = wishlistItems.length
                const topItem = wishlistItems.find(j => j.priority === 'high') || wishlistItems[0]
                return (
                  <div
                    onClick={() => { setActiveTab('services'); setJobFilter('wishlist') }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FBF3E8' }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.card }}
                    style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '16px', boxShadow: '0 2px 8px rgba(44,29,20,0.04)', cursor: 'pointer', transition: 'background 0.15s', position: 'relative' }}
                  >
                    <div style={{ marginBottom: '6px', fontSize: '20px', lineHeight: 1 }}>⭐</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: C.brown, fontFamily: f }}>{wlCount}</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif', marginTop: '1px' }}>Wishlist</div>
                    <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {wlCount === 0 ? 'Add home upgrades' : topItem ? topItem.title : `${wlCount} item${wlCount !== 1 ? 's' : ''}`}
                    </div>
                    <div style={{ position: 'absolute', bottom: '10px', right: '12px', fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>→</div>
                  </div>
                )
              })()}
              {/* 3–9. Remaining stat cards */}
              {[
                { label: 'Annual Spend', value: annualSpend > 0 ? `$${Math.round(annualSpend)}` : '$0', Icon: DollarSign, sub: annualSaved > 0 ? `$${Math.round(annualSaved)} saved vs avg` : 'Log task costs to track', savedSub: annualSaved > 0, action: annualSpend > 0 ? () => setActiveTab('home_profile') : null },
                { label: 'Appliances', value: appliances.length, Icon: Package, sub: appliances.length === 0 ? 'Tap to add your first' : `${appliances.filter(a => a.status === 'good').length} up to date`, action: () => setActiveTab('home_profile') },
                { label: 'Home Systems', value: systems.length, Icon: Wrench, sub: systems.length === 0 ? 'Tap to add a system' : `${systems.filter(s => s.status === 'good').length} up to date`, action: () => setActiveTab('home_profile') },
                {
                  label: 'Parts Due',
                  value: dueParts.length,
                  Icon: ShoppingCart,
                  sub: dueParts.length === 0
                    ? autoOrderItems.length > 0 ? `${autoOrderItems.length} trackable parts` : 'Attach parts to appliances'
                    : `${dueParts.length} part${dueParts.length !== 1 ? 's' : ''} due or coming up`,
                  action: dueParts.length > 0 ? () => {
                    const el = document.getElementById('parts-supplies-section')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  } : null,
                },
                { label: 'Open Tasks', value: allScheduleItems.length, Icon: ClipboardList, sub: `${diyItems.length} DIY · ${contractorItems.length} contractor` },
                { label: 'Services', value: recurringServices.length, Icon: RefreshCw, sub: recurringServices.length === 0 ? 'Tap to add a service' : `${recurringServices.length} recurring`, action: () => setActiveTab('services') },
                { label: 'Energy Score', value: energyGrade, Icon: Leaf, sub: energyCertifiedCount > 0 ? `${energyCertifiedCount} ENERGY STAR certified` : energyTotalCost > 0 ? `~$${energyTotalCost}/yr est. cost` : 'Tap to see details', action: () => setShowEnergyModal(true), valueColor: energyGradeColor },
              ].map((stat, i) => {
                const StatIcon = stat.Icon
                return (
                <div
                  key={i}
                  onClick={stat.action || undefined}
                  onMouseEnter={e => { if (stat.action) e.currentTarget.style.background = '#FBF3E8' }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.card }}
                  style={{
                    background: C.card, borderRadius: '12px',
                    border: `1px solid ${C.border}`, padding: '16px',
                    boxShadow: '0 2px 8px rgba(44,29,20,0.04)',
                    cursor: stat.action ? 'pointer' : 'default',
                    transition: 'background 0.15s', position: 'relative',
                  }}
                >
                  <div style={{ marginBottom: '6px' }}><StatIcon size={20} strokeWidth={1.5} color={C.amber} /></div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: stat.valueColor || C.brown, fontFamily: f }}>{stat.value}</div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif', marginTop: '1px' }}>{stat.label}</div>
                  <div style={{ fontSize: '10px', color: stat.savedSub ? C.success : C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>{stat.sub}</div>
                  {stat.action && <div style={{ position: 'absolute', bottom: '10px', right: '12px', fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>→</div>}
                </div>
                )
              })}
            </div>
          </div>

          {/* Parts & Supplies */}
          {dueParts.length > 0 && (
            <div id="parts-supplies-section" style={{ marginBottom: '28px' }}>
              <div style={S.sectionTitle}>Parts & Supplies Due</div>
              <div style={{
                background: C.card, borderRadius: '12px',
                border: `1px solid ${C.border}`, overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(44,29,20,0.05)',
              }}>
                {dueParts.map((part, i) => {
                  const catalog = part.parts_catalog
                  const name = part.custom_name || catalog?.name || 'Part'
                  const price = part.custom_price ?? catalog?.unit_price
                  const asin = part.custom_asin || catalog?.amazon_asin
                  const hasLink = !!(asin || part.custom_url || catalog?.amazon_url)
                  const isOrdered = !!orderedParts[part.id]

                  const today = new Date(); today.setHours(0, 0, 0, 0)
                  const dueDate = part.next_due_date ? new Date(part.next_due_date) : null
                  const diffDays = dueDate ? Math.ceil((dueDate - today) / 86400000) : null
                  const isOverdue = diffDays !== null && diffDays < 0
                  const badgeBg = isOverdue ? '#FDECEA' : '#FBF3E8'
                  const badgeColor = isOverdue ? C.danger : C.amber
                  const badgeLabel = isOverdue
                    ? 'Overdue'
                    : diffDays === 0 ? 'Due today'
                    : `Due in ${diffDays}d`

                  const appliance = appliances.find(a => a.asset_id === part.asset_id)
                  const system = systems.find(s => s.id === part.system_id)
                  const sourceName = appliance?.nickname || appliance?.name || system?.name || ''

                  return (
                    <div key={part.id} style={{
                      padding: '14px 16px',
                      borderBottom: i < dueParts.length - 1 ? `1px solid ${C.border}` : 'none',
                      display: 'flex', alignItems: 'center', gap: '12px',
                    }}>
                      <ShoppingBag size={20} strokeWidth={1.5} color={C.amber} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown }}>{name}</div>
                        {sourceName && (
                          <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                            {sourceName}
                          </div>
                        )}
                      </div>
                      {price != null && (
                        <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown, flexShrink: 0 }}>
                          ${parseFloat(price).toFixed(2)}
                        </div>
                      )}
                      <div style={{
                        fontSize: '10px', padding: '3px 8px', borderRadius: '20px',
                        background: badgeBg, color: badgeColor,
                        fontFamily: 'Calibri, sans-serif',
                        border: `1px solid ${badgeColor}44`,
                        flexShrink: 0, whiteSpace: 'nowrap',
                      }}>
                        {badgeLabel}
                      </div>
                      {hasLink && (
                        <button
                          disabled={isOrdered}
                          onClick={() => handleOrderNow(part)}
                          style={{
                            background: isOrdered
                              ? '#EBF5EF'
                              : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                            border: 'none', borderRadius: '8px',
                            padding: '7px 14px', fontSize: '12px', fontWeight: '700',
                            color: isOrdered ? C.success : C.card,
                            cursor: isOrdered ? 'default' : 'pointer',
                            fontFamily: f, whiteSpace: 'nowrap', flexShrink: 0,
                            letterSpacing: '0.5px',
                            display: 'flex', alignItems: 'center', gap: '5px',
                            transition: 'opacity 0.15s',
                          }}
                        >
                          {isOrdered
                            ? '✓ Ordered'
                            : <><ExternalLink size={11} strokeWidth={2} /> Order Now</>
                          }
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              <div style={{
                fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif',
                marginTop: '6px', textAlign: 'right',
              }}>
                Orders fulfilled via Amazon · Casavio may earn a small affiliate commission
              </div>
            </div>
          )}

          {/* Maintenance breakdown */}
          <div id="maintenance-breakdown" />
          {allMaintenanceItems.length === 0 ? (
            <div style={{ background: C.card, borderRadius: '14px', border: `1px solid ${C.border}`, padding: '32px', textAlign: 'center', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}><House size={36} strokeWidth={1} color={C.amber} /></div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>Your maintenance schedule will appear here</div>
              <div style={{ fontSize: '14px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px' }}>Add appliances to automatically generate your personalized schedule.</div>
              <button style={S.addBtn} onClick={() => setActiveTab('home_profile')}>Add Your First Appliance</button>
            </div>
          ) : (
            <>
              <div style={S.sectionTitle}>Maintenance Schedule</div>
              <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(44,29,20,0.05)', padding: '0 16px', marginBottom: '16px' }}>
                {allMaintenanceItems.map((item, i) => (
                  <MaintenanceTaskTile
                    key={`${item.itemType}-${item.itemId}-${item.task}`}
                    task={{ task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }}
                    nextDue={item.nextDue}
                    status={item.status}
                    isCompleted={false}
                    lastCompleted={item.lastCompleted}
                    applianceName={item.itemName}
                    showApplianceName={true}
                    onCheck={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOpen={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOrderNow={getOrderNow(item)}
                    isLast={i === allMaintenanceItems.length - 1}
                  />
                ))}
              </div>
            </>
          )}

          {/* Print button */}
          {(appliances.length > 0 || systems.length > 0) && (
            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button
                style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '12px', color: C.muted, cursor: 'pointer', fontFamily: f, letterSpacing: '1px' }}
                onClick={() => setShowPrint(true)}
              >
                📄 Export Home Summary
              </button>
            </div>
          )}
        </div>
      )}

      {/* ════════ HOME PROFILE TAB ════════ */}
      {activeTab === 'home_profile' && (
        <div style={S.content} className="casavio-content">
          <style>{`
            .casavio-empty-cta {
              cursor: pointer;
              transition: background 0.15s, box-shadow 0.15s, border-color 0.15s;
            }
            .casavio-empty-cta:hover {
              background: #F5EEDF;
              border-color: ${C.amber};
              box-shadow: 0 2px 8px rgba(184,114,31,0.12);
            }
            .casavio-empty-cta:focus-visible {
              outline: 2px solid ${C.amber};
              outline-offset: 2px;
            }
          `}</style>
          <div style={{ fontSize: '26px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>Home Profile</div>
          <div style={{ fontSize: '14px', color: C.muted, marginBottom: '28px' }}>
            {appliances.length + systems.length === 0
              ? 'Your home details, documents, appliances, and systems'
              : `${appliances.length} appliance${appliances.length !== 1 ? 's' : ''} · ${systems.length} system${systems.length !== 1 ? 's' : ''}`}
          </div>

          {/* Home Profile Card — inline expandable */}
          <div style={{ background: C.card, borderRadius: '14px', border: `1px solid ${C.border}`, marginBottom: '28px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)', overflow: 'hidden' }}>
            {!editingProfile ? (
              <div style={{ padding: '20px 24px' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                    <House size={18} strokeWidth={1.5} color={C.amber} style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {resolveDisplayAddress(hp) || 'No address set'}
                    </div>
                  </div>
                  {hpFilledCount > 2 && (
                    <button
                      style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '5px 12px', fontSize: '11px', color: C.amber, cursor: 'pointer', fontFamily: f, letterSpacing: '1px', flexShrink: 0, marginLeft: '12px' }}
                      onClick={openProfileEditForm}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {/* Property detail chips */}
                {(hp.property_type || hp.year_built || hp.bedrooms || hp.bathrooms || hp.sq_footage) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginBottom: '10px', paddingLeft: '28px' }}>
                    {hp.property_type && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{profPtLabels[hp.property_type] || hp.property_type}</span>{(countyAppliedFields.has('property_type') || countyAppliedFields.has('verified_property_type')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.year_built && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Built {hp.year_built}</span>{(countyAppliedFields.has('year_built') || countyAppliedFields.has('verified_year_built')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.bedrooms && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{hp.bedrooms} bed</span>{(countyAppliedFields.has('bedrooms') || countyAppliedFields.has('verified_bedrooms')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.bathrooms && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{hp.bathrooms} bath</span>{(countyAppliedFields.has('bathrooms') || countyAppliedFields.has('verified_bathrooms')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.sq_footage && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{Number(hp.sq_footage).toLocaleString()} sq ft</span>{(countyAppliedFields.has('sq_footage') || countyAppliedFields.has('verified_sq_footage')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.lot_size && <span><span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{hp.lot_size}</span>{(countyAppliedFields.has('lot_size') || countyAppliedFields.has('verified_lot_size')) && <CountyPill enrichedAt={homeProfile?.county_enriched_at} />}</span>}
                    {hp.hoa && hp.hoa_fee && <span style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>HOA ${hp.hoa_fee}/mo</span>}
                  </div>
                )}

                {/* Complete prompt if sparse */}
                {hpFilledCount <= 2 && (
                  <div
                    style={{ paddingLeft: '28px', paddingBottom: '6px', cursor: 'pointer' }}
                    onClick={() => { setHomeProfileForm({ ...hp, addr_street: hp.display_address || hp.address || '', addr_city: hp.display_city || hp.city || '', addr_state: hp.display_state || hp.state || '', addr_zip: hp.display_zip || hp.zip || '' }); setEditingProfile(true) }}
                  >
                    <span style={{ fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>+ Complete your profile →</span>
                  </div>
                )}

                {/* Progress bar */}
                <div style={{ paddingLeft: '28px', paddingTop: '6px' }}>
                  <div style={{ height: '3px', borderRadius: '2px', background: C.border, marginBottom: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '3px', borderRadius: '2px', background: C.amber, width: `${hpCompleteness}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                    Profile {hpFilledCount}/{totalHpFields} complete
                    {profileSaved && <span style={{ color: C.success, marginLeft: '8px' }}>✓ Saved</span>}
                  </div>
                  {activeHomeId && hp.county_enrichment_status !== 'success' && hp.county_enrichment_status !== 'partial' && (
                    <div style={{ background: '#FBF3E8', padding: '8px 12px', marginTop: '12px', fontSize: '12px', fontFamily: 'Calibri, sans-serif', color: C.brown, lineHeight: 1.5, borderRadius: '6px' }}>
                      {!hp.county_enriched_at && (
                        <>Your home isn't connected to county records yet. Tap <strong>Edit → 🏛️ Auto-fill</strong> to pull them in seconds.</>
                      )}
                      {hp.county_enrichment_status === 'unsupported_county' && (
                        <>Your county isn't auto-connected yet, but don't worry — tap <strong>Edit</strong> and we'll guide you through it step-by-step.</>
                      )}
                      {hp.county_enrichment_status === 'failed' && (
                        <>Last auto-fill attempt didn't find your home (attempted {new Date(hp.county_enriched_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}). Tap <strong>Edit</strong> to try again.</>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── Inline Edit Form ── */
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown, flexShrink: 0 }}>Edit Home Profile</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, justifyContent: 'flex-end' }}>
                    {hp.county_enriched_at && (hp.county_enrichment_status === 'success' || hp.county_enrichment_status === 'partial') ? (
                      <>
                        <span style={{ fontSize: '11px', fontFamily: 'Calibri, sans-serif', color: C.success }}>✓ Pulled from {hp.county_data_source || 'county records'} on {new Date(hp.county_enriched_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}</span>
                        <button disabled={countyEnrichLoading} onClick={runCountyEnrich} style={{ background: 'none', border: 'none', padding: 0, fontSize: '11px', color: countyEnrichLoading ? C.muted : C.amber, cursor: countyEnrichLoading ? 'not-allowed' : 'pointer', fontFamily: 'Calibri, sans-serif', textDecoration: 'underline' }}>
                          {countyEnrichLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button disabled={countyEnrichLoading} onClick={runCountyEnrich} style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', color: countyEnrichLoading ? C.muted : C.amber, cursor: countyEnrichLoading ? 'not-allowed' : 'pointer', fontFamily: f, letterSpacing: '0.3px' }}>
                          {countyEnrichLoading ? 'Pulling…' : '🏛️ Auto-fill'}
                        </button>
                        <span
                          title={hp.county_enrichment_status === 'unsupported_county'
                            ? "Casavio doesn't auto-connect to this county yet, but we can walk you through pulling your records manually — faster if you have your parcel number handy."
                            : "Casavio can pull your home's official details — year built, square footage, lot size, sale history, and more — directly from your county assessor."}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', borderRadius: '50%', background: '#FBF3E8', color: C.brown, fontSize: '10px', fontWeight: '600', fontFamily: 'Calibri, sans-serif', cursor: 'help', fontStyle: 'italic' }}
                        >ⓘ</span>
                      </>
                    )}
                  </div>
                  <button style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: 0, lineHeight: 1, marginLeft: '8px', flexShrink: 0 }} onClick={closeProfileEditForm}>×</button>
                </div>

                {/* County auto-fill result banner */}
                {countyEnrichBanner && (
                  <div style={{ background: countyEnrichBanner.type === 'success' ? '#EBF5EF' : countyEnrichBanner.type === 'warning' ? '#FBF3E8' : '#FDECEA', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', fontFamily: 'Calibri, sans-serif', color: countyEnrichBanner.type === 'success' ? C.success : countyEnrichBanner.type === 'warning' ? C.amber : '#B03020', lineHeight: 1.5 }}>
                    {countyEnrichBanner.message}
                  </div>
                )}

                <div style={{ fontSize: '11px', color: '#9A7B68', fontFamily: 'Calibri, sans-serif', lineHeight: 1.5, marginBottom: '14px' }}>
                  Pick from suggestions to verify your address (used for 911 dispatch and county records). Or just type to customize how your address displays.
                </div>

                <label style={profLbl}>Street Address</label>
                <AddressAutocomplete
                  value={homeProfileForm.addr_street || ''}
                  onChange={(v) => setHomeProfileForm(p => ({ ...p, addr_street: v }))}
                  onSelect={({ address, city, state, zip, latitude, longitude, placeId }) => {
                    // Stage the accepted address into the form ONLY. The DB write
                    // happens on the explicit Save click (saveHomeProfile), never
                    // here — the immediate write here was the silent-overwrite
                    // corruption vector.
                    setHomeProfileForm(prev => ({
                      ...prev,
                      address: address || prev.address,
                      city: city || prev.city,
                      state: state || prev.state,
                      zip: zip || prev.zip,
                      latitude: latitude ?? null,
                      longitude: longitude ?? null,
                      google_place_id: placeId ?? null,
                      addr_street: address || prev.address || '',
                      addr_city: city || prev.city || '',
                      addr_state: state || prev.state || '',
                      addr_zip: zip || prev.zip || '',
                      display_address: null,
                      display_city: null,
                      display_state: null,
                      display_zip: null,
                    }))
                  }}
                  placeholder="Street address"
                  inputStyle={{ ...profInp, marginBottom: '4px' }}
                />
                {addrHint(homeProfileForm.addr_street, homeProfileForm.address)}

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '4px' }}>
                  <div>
                    <label style={profLbl}>City</label>
                    <input style={{ ...profInp, marginBottom: '4px' }} value={homeProfileForm.addr_city || ''} onChange={e => setHomeProfileForm(p => ({ ...p, addr_city: e.target.value }))} placeholder="City" autoComplete="off" />
                    {addrHint(homeProfileForm.addr_city, homeProfileForm.city)}
                  </div>
                  <div>
                    <label style={profLbl}>State</label>
                    <input style={{ ...profInp, marginBottom: '4px' }} value={homeProfileForm.addr_state || ''} onChange={e => setHomeProfileForm(p => ({ ...p, addr_state: e.target.value }))} placeholder="CA" maxLength={2} autoComplete="off" />
                    {addrHint(homeProfileForm.addr_state, homeProfileForm.state)}
                  </div>
                  <div>
                    <label style={profLbl}>ZIP</label>
                    <input style={{ ...profInp, marginBottom: '4px' }} value={homeProfileForm.addr_zip || ''} onChange={e => setHomeProfileForm(p => ({ ...p, addr_zip: e.target.value }))} placeholder="94945" maxLength={10} autoComplete="off" />
                    {addrHint(homeProfileForm.addr_zip, homeProfileForm.zip)}
                  </div>
                </div>
                <div style={{ marginBottom: '8px' }} />

                <label style={profLbl}>Property Type</label>
                <select style={{ ...profInp, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', marginBottom: '12px' }} value={homeProfileForm.property_type || ''} onChange={e => setHomeProfileForm(p => ({ ...p, property_type: e.target.value }))}>
                  <option value="">Select type...</option>
                  {[['single_family','Single Family Home'],['condo','Condo / Townhome'],['multi_family','Multi-Family'],['mobile','Mobile Home'],['vacation','Vacation Home'],['other','Other']].map(([val, lbl]) => (
                    <option key={val} value={val}>{lbl}</option>
                  ))}
                </select>

                <label style={profLbl}>Year Built</label>
                <select style={{ ...profInp, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', marginBottom: '12px' }} value={homeProfileForm.year_built || ''} onChange={e => setHomeProfileForm(p => ({ ...p, year_built: e.target.value }))}>
                  <option value="">Select year...</option>
                  {Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div>
                    <label style={profLbl}>Bedrooms</label>
                    <select style={{ ...profInp, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }} value={homeProfileForm.bedrooms || ''} onChange={e => setHomeProfileForm(p => ({ ...p, bedrooms: e.target.value }))}>
                      <option value="">Select...</option>
                      {['Studio','1','2','3','4','5','6','7','8','9','10+'].map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={profLbl}>Bathrooms</label>
                    <select style={{ ...profInp, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }} value={homeProfileForm.bathrooms || ''} onChange={e => setHomeProfileForm(p => ({ ...p, bathrooms: e.target.value }))}>
                      <option value="">Select...</option>
                      {['1','1.5','2','2.5','3','3.5','4','4.5','5+'].map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>

                <label style={profLbl}>Square Footage</label>
                <input style={{ ...profInp, marginBottom: '12px' }} type="number" placeholder="e.g. 1850" value={homeProfileForm.sq_footage || ''} onChange={e => setHomeProfileForm(p => ({ ...p, sq_footage: e.target.value }))} />

                <label style={profLbl}>Lot Size <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input style={{ ...profInp, marginBottom: '12px' }} type="text" placeholder="e.g. 0.25 acres" value={homeProfileForm.lot_size || ''} onChange={e => setHomeProfileForm(p => ({ ...p, lot_size: e.target.value }))} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label style={{ ...profLbl, margin: 0 }}>HOA</label>
                  <div
                    onClick={() => setHomeProfileForm(p => ({ ...p, hoa: !p.hoa }))}
                    style={{ width: '40px', height: '22px', borderRadius: '11px', background: homeProfileForm.hoa ? C.amber : C.border, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
                  >
                    <div style={{ position: 'absolute', top: '1px', left: homeProfileForm.hoa ? '19px' : '1px', width: '20px', height: '20px', borderRadius: '50%', background: C.card, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>

                {homeProfileForm.hoa && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '12px' }}>
                    <div>
                      <label style={profLbl}>HOA Name</label>
                      <input style={profInp} placeholder="Harbor View HOA" value={homeProfileForm.hoa_name || ''} onChange={e => setHomeProfileForm(p => ({ ...p, hoa_name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={profLbl}>Monthly Fee</label>
                      <input style={profInp} type="number" placeholder="250" value={homeProfileForm.hoa_fee || ''} onChange={e => setHomeProfileForm(p => ({ ...p, hoa_fee: e.target.value }))} />
                    </div>
                  </div>
                )}

                <button
                  style={{ width: '100%', background: profileSaving ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '10px', padding: '13px', color: profileSaving ? C.muted : C.card, fontSize: '14px', fontWeight: '700', cursor: profileSaving ? 'not-allowed' : 'pointer', fontFamily: f, letterSpacing: '1px', textTransform: 'uppercase', marginTop: '8px' }}
                  onClick={saveHomeProfile}
                  disabled={profileSaving}
                >
                  {profileSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {/* ── HOME DETAILS ── */}
          {(() => {
            const getRoomEmoji = (type) => ({ bedroom: '🛏️', bathroom: '🚿', kitchen: '🍳', living: '🛋️', dining: '🪑', garage: '🚗', office: '💻', den: '📺', pantry: '🥫', closet: '👗', hallway: '🚪', other: '🏠' }[type] || '🏠')
            const getReorderUrl = (retailer, brand, colorName, colorCode) => {
              const query = encodeURIComponent(`${brand || ''} ${colorName || ''} ${colorCode || ''}`.trim())
              const urls = { 'Sherwin-Williams': `https://www.sherwin-williams.com/search?q=${query}`, 'Benjamin Moore': `https://www.benjaminmoore.com/en-us/color-overview/find-your-color?q=${query}`, 'Behr': `https://www.behr.com/consumer/colors/paint/?q=${query}` }
              return urls[retailer] || '#'
            }
            const ReorderLinks = ({ brand, color, code }) => {
              if (!code && !brand) return null
              return (
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>🛒 Reorder:</span>
                  {['Sherwin-Williams', 'Benjamin Moore', 'Behr'].map(r => (
                    <a key={r} href={getReorderUrl(r, brand, color, code)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: C.amber, textDecoration: 'none', fontFamily: 'Calibri, sans-serif' }}>{r} →</a>
                  ))}
                </div>
              )
            }
            const ExField = ({ label, field, type: fType = 'text', options }) => {
              const val = hp[field]
              if (!editingExterior) {
                return (
                  <div>
                    <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '3px' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: val ? C.brown : C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: val ? 'normal' : 'italic' }}>{val || '—'}</div>
                  </div>
                )
              }
              if (fType === 'select') {
                return (
                  <div>
                    <label style={{ fontSize: '10px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '3px', display: 'block' }}>{label}</label>
                    <select style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: C.brown, boxSizing: 'border-box', outline: 'none', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }} value={exteriorForm[field] || ''} onChange={e => setExteriorForm(p => ({ ...p, [field]: e.target.value }))}>
                      <option value="">—</option>
                      {options.map(o => <option key={o} value={o.toLowerCase().replace(/[^a-z0-9]/g, '_')}>{o}</option>)}
                    </select>
                  </div>
                )
              }
              return (
                <div>
                  <label style={{ fontSize: '10px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '3px', display: 'block' }}>{label}</label>
                  <input style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: C.brown, boxSizing: 'border-box', outline: 'none' }} type={fType} value={exteriorForm[field] || ''} onChange={e => setExteriorForm(p => ({ ...p, [field]: e.target.value }))} />
                </div>
              )
            }
            const exteriorSummary = (() => {
              const parts = []
              if (hp.exterior_paint_color) parts.push(hp.exterior_paint_color)
              if (hp.roof_material) parts.push(hp.roof_material)
              if (hp.front_door_color) parts.push(`Door: ${hp.front_door_color}`)
              return parts.join(' · ') || 'Tap to add exterior details'
            })()

            const RoomTile = ({ room }) => {
              const dimDisplay = room.dimension_length && room.dimension_width
                ? `${room.dimension_length}×${room.dimension_width}ft`
                : room.sq_footage ? `${room.sq_footage} sq ft` : null
              const hasDetails = !!(room.wall_paint_color || room.flooring_type || room.dimension_length)
              return (
                <div
                  onClick={() => setSelectedRoom(room)}
                  style={{ background: C.card, borderRadius: '12px', padding: '14px 10px', cursor: 'pointer', border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
                >
                  <div style={{ width: 48, height: 48, background: C.bg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '8px' }}>
                    {getRoomEmoji(room.type)}
                  </div>
                  <div style={{ fontFamily: f, fontSize: '13px', color: C.brown, marginBottom: '2px' }}>{room.name}</div>
                  {dimDisplay && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '1px' }}>{dimDisplay}</div>}
                  {room.flooring_type && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '1px' }}>{room.flooring_type}</div>}
                  <div style={{ fontSize: '11px', color: hasDetails ? C.success : C.muted, marginTop: '4px', fontFamily: 'Calibri, sans-serif' }}>
                    {hasDetails ? '✓ Details added' : 'Tap to add details'}
                  </div>
                </div>
              )
            }

            return (
              <>
                {/* Exterior */}
                <CollapsibleTile
                  emoji="🏡"
                  title="Exterior"
                  summary={exteriorSummary}
                  expanded={exteriorExpanded}
                  onToggle={() => setExteriorExpanded(p => !p)}
                >
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <button
                      onClick={() => {
                        if (editingExterior) { saveExterior() } else { setExteriorForm({ ...hp }); setEditingExterior(true) }
                      }}
                      style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: '13px', fontFamily: 'Calibri, sans-serif', fontWeight: '600', padding: 0 }}
                    >{exteriorSaving ? 'Saving...' : editingExterior ? 'Save' : 'Edit'}</button>
                  </div>

                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Exterior Paint</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px' }}>
                      <ExField label="Brand" field="exterior_paint_brand" />
                      <ExField label="Color Name" field="exterior_paint_color" />
                      <ExField label="Color Code" field="exterior_paint_code" />
                      <ExField label="Finish" field="exterior_paint_finish" type="select" options={['Flat','Eggshell','Satin','Semi-Gloss','Gloss']} />
                    </div>
                    <ReorderLinks brand={hp.exterior_paint_brand} color={hp.exterior_paint_color} code={hp.exterior_paint_code} />

                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px', marginTop: '14px' }}>Exterior Trim Paint</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px' }}>
                      <ExField label="Brand" field="exterior_trim_brand" />
                      <ExField label="Color Name" field="exterior_trim_color" />
                      <ExField label="Color Code" field="exterior_trim_code" />
                      <ExField label="Finish" field="exterior_trim_finish" type="select" options={['Flat','Eggshell','Satin','Semi-Gloss','Gloss']} />
                    </div>
                    <ReorderLinks brand={hp.exterior_trim_brand} color={hp.exterior_trim_color} code={hp.exterior_trim_code} />

                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px', marginTop: '14px' }}>Roof</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                      <ExField label="Material" field="roof_material" type="select" options={['Asphalt Shingle','Tile','Metal','Wood Shake','Flat/TPO','Other']} />
                      <ExField label="Color" field="roof_color" />
                      <ExField label="Year Installed" field="roof_year_installed" type="number" />
                    </div>

                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Other</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                      <ExField label="Front Door Color" field="front_door_color" />
                      <ExField label="Garage Door Color" field="garage_door_color" />
                      <ExField label="Driveway Material" field="driveway_material" type="select" options={['Concrete','Asphalt','Pavers','Gravel','Other']} />
                      <ExField label="Fence Material" field="fence_material" type="select" options={['Wood','Vinyl','Metal','Brick','None','Other']} />
                    </div>

                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Other Notes</div>
                    <textarea
                      value={editingExterior ? (exteriorForm.exterior_notes || '') : (hp.exterior_notes || '')}
                      onChange={e => {
                        if (editingExterior) setExteriorForm(p => ({ ...p, exterior_notes: e.target.value }))
                        else setHomeProfile(prev => ({ ...prev, exterior_notes: e.target.value }))
                      }}
                      onBlur={e => { if (!editingExterior && activeHomeId) supabase.from('homes').update({ exterior_notes: e.target.value || null }).eq('id', activeHomeId) }}
                      placeholder="HOA color restrictions, paint year, contractor used, warranty info…"
                      style={{ width: '100%', minHeight: '80px', padding: '10px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', fontFamily: 'Calibri, sans-serif', fontSize: '13px', color: C.brown, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                    />
                </CollapsibleTile>

                {/* Rooms — grid matching appliance tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                  {rooms.map(room => <RoomTile key={room.id} room={room} />)}
                  <div
                    onClick={() => setShowAddRoom(true)}
                    style={{ background: 'transparent', borderRadius: '12px', padding: '14px 10px', cursor: 'pointer', border: `2px dashed ${C.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '110px' }}
                  >
                    <div style={{ fontSize: '24px', color: C.muted, marginBottom: '6px' }}>+</div>
                    <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Add room</div>
                  </div>
                </div>

                {/* Interior Notes */}
                <CollapsibleTile
                  emoji="📝"
                  title="Interior Notes"
                  summary={hp?.interior_notes ? hp.interior_notes.substring(0, 60) + (hp.interior_notes.length > 60 ? '…' : '') : 'Renovation history, special features, HOA restrictions'}
                  expanded={interiorNotesExpanded}
                  onToggle={() => setInteriorNotesExpanded(p => !p)}
                  style={{ marginBottom: '12px' }}
                >
                  <textarea
                    value={hp.interior_notes || ''}
                    onChange={e => setHomeProfile(prev => ({ ...prev, interior_notes: e.target.value }))}
                    onBlur={e => saveInteriorNotes(e.target.value)}
                    placeholder="Renovation history, special features, HOA restrictions, paint notes…"
                    style={{ width: '100%', minHeight: '100px', padding: '12px 14px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', fontFamily: 'Calibri, sans-serif', fontSize: '13px', color: C.brown, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                  />
                </CollapsibleTile>

                {/* County Records */}
                {(() => {
                  const county = getCounty(hp)
                  const countyLinks = getCountyRecordsLinks(county)
                  if (!countyLinks) return null
                  const countyLabel = county ? county.charAt(0).toUpperCase() + county.slice(1) : ''
                  return (
                    <CollapsibleTile
                      emoji="🏛️"
                      title="County Records"
                      summary={`${countyLabel} County — assessor, recorder, permits`}
                      expanded={countyRecordsExpanded}
                      onToggle={() => setCountyRecordsExpanded(p => !p)}
                      style={{ marginBottom: '28px' }}
                    >
                      {countyLinks.map(link => (
                        <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}`, textDecoration: 'none', color: C.brown }}>
                          <span style={{ fontSize: '13px', fontFamily: 'Calibri, sans-serif' }}>{link.label}</span>
                          <span style={{ color: C.amber, fontSize: '13px' }}>→</span>
                        </a>
                      ))}
                    </CollapsibleTile>
                  )
                })()}
              </>
            )
          })()}

          {/* Document Vault */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={S.sectionTitle}>Documents</div>
            <button style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '10px 16px', fontSize: '11px', color: C.amber, cursor: 'pointer', fontFamily: f, letterSpacing: '1px' }} onClick={() => setShowDocumentVault(true)}>Open Vault</button>
          </div>
          <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '16px 20px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }} onClick={() => setShowDocumentVault(true)}>
            <FolderOpen size={22} strokeWidth={1.5} color={C.amber} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown }}>Document Vault</div>
              <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Warranties, manuals, insurance, service records & more</div>
            </div>
            <div style={{ marginLeft: 'auto', color: C.amber }}>→</div>
          </div>

          {/* Appliances section */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={S.sectionTitle}>Appliances</div>
            <button data-testid="btn-add-appliance" style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '10px 16px', fontSize: '11px', color: C.amber, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' }} onClick={() => setShowAdd(true)}>+ Add appliance</button>
          </div>
          {!loading && activeHomeId && (
            <StandardAppliancesPrompt
              appliances={appliances}
              dismissedKeys={homeProfile?.dismissed_standard_appliances || []}
              onAdd={(typeId) => { setAddInitialType(typeId); setShowAdd(true) }}
              onDismiss={dismissStandardSlot}
            />
          )}
          {loading ? <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '13px', padding: '12px 0', marginBottom: '24px' }}>Loading...</div>
          : appliances.length === 0 ? (
            <div
              className="casavio-empty-cta"
              style={{ background: C.bg, border: `1px dashed ${C.border}`, borderRadius: '12px', padding: '24px', textAlign: 'center', marginBottom: '28px' }}
              role="button"
              tabIndex={0}
              aria-label="Add appliance"
              onClick={() => setShowAdd(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAdd(true) } }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Package size={32} strokeWidth={1} color={C.muted} /></div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '6px' }}>No appliances yet</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>Add your refrigerator, HVAC, washer, dryer, and more.</div>
              <button style={S.addBtn} onClick={(e) => { e.stopPropagation(); setShowAdd(true) }}>+ Add Appliance</button>
            </div>
          ) : (
            <>
              <div style={S.grid}>
                {appliances.map(a => {
                  const AIcon = applianceIcons[a.type] || Wrench
                  const isIncomplete = !a.make || !a.model || !(Array.isArray(a.photos) && a.photos.length > 0)
                  return (
                  <div
                    key={a.id}
                    style={S.appCard(a.status)}
                    onClick={(e) => {
                      // Bail if the click originated inside the Incomplete badge — defensive
                      // backup to e.stopPropagation() on the badge itself.
                      if (e.target?.closest?.('[data-incomplete-badge="1"]')) return
                      setSelectedAppliance(a)
                    }}
                  >
                    {isIncomplete && (
                      <button
                        type="button"
                        data-incomplete-badge="1"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setSelectedAppliance(null)
                          setGuidedAppliance(a)
                        }}
                        style={{ ...S.incompleteBadge, cursor: 'pointer' }}
                        title="Tap to complete documentation"
                      >
                        <span style={S.incompleteBadgeTop}>Incomplete</span>
                        <span style={S.incompleteBadgeBottom}>tap to finish →</span>
                      </button>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}><AIcon size={28} strokeWidth={1.5} color={C.amber} /></div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>{a.nickname || a.name}</div>
                    <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '4px' }}>{a.make} {a.model}</div>
                    {a.location && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}><MapPin size={10} strokeWidth={1.5} color={C.muted} />{a.location}</div>}
                    <div style={{ fontSize: '11px', color: a.status === 'good' ? C.success : a.status === 'due' ? C.amber : C.danger, fontFamily: 'Calibri, sans-serif' }}>
                      {a.status === 'good' ? '✓ Up to date' : a.status === 'due' ? '⚠ Service due' : '✗ Overdue'}
                    </div>
                    {a.energy_star === true && (
                      <div style={{ fontSize: '9px', color: C.success, background: '#EBF5EF', border: `1px solid ${C.success}44`, borderRadius: '10px', padding: '2px 6px', marginTop: '4px', display: 'inline-block', fontFamily: 'Calibri, sans-serif', fontWeight: '700', letterSpacing: '0.3px' }}>ENERGY STAR</div>
                    )}
                  </div>
                  )
                })}
                <div style={S.addCard} onClick={() => setShowAdd(true)}>
                  <div style={{ fontSize: '24px', color: C.muted, marginBottom: '4px' }}>+</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', textAlign: 'center' }}>Add appliance</div>
                </div>
              </div>
            </>
          )}

          {/* Divider */}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: '8px 0 28px' }} />

          {/* Systems section */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={S.sectionTitle}>Home Systems</div>
            <button style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '10px 16px', fontSize: '11px', color: C.amber, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' }} onClick={() => setShowAddSystem(true)}>+ Add system</button>
          </div>
          {systemsLoading ? <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '13px', padding: '12px 0', marginBottom: '24px' }}>Loading...</div>
          : systems.length === 0 ? (
            <div
              className="casavio-empty-cta"
              style={{ background: C.bg, border: `1px dashed ${C.border}`, borderRadius: '12px', padding: '24px', textAlign: 'center', marginBottom: '28px' }}
              role="button"
              tabIndex={0}
              aria-label="Add home system"
              onClick={() => setShowAddSystem(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAddSystem(true) } }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Wrench size={32} strokeWidth={1} color={C.muted} /></div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '6px' }}>No systems yet</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>Add septic, solar, pool, generator, electrical panel and more.</div>
              <button style={S.addBtn} onClick={(e) => { e.stopPropagation(); setShowAddSystem(true) }}>+ Add Home System</button>
            </div>
          ) : (
            <div style={S.grid}>
              {systems.map(s => {
                const SIcon = systemIcons[s.type] || Wrench
                return (
                <div key={s.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }} onClick={() => setSelectedSystem(s)}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><SIcon size={32} strokeWidth={1.5} color={C.amber} /></div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>{s.name}</div>
                  {s.source === 'county_enrichment' && (
                    <div title="This system was added automatically from your county's assessor records. Click to edit or remove if it's not accurate." style={{ display: 'inline-block', background: '#FBF3E8', color: C.brown, fontSize: '11px', fontFamily: 'Calibri, sans-serif', borderRadius: '10px', padding: '4px 8px', marginBottom: '4px' }}>From county records — verify</div>
                  )}
                  {(s.manufacturer || s.model) && <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '4px' }}>{s.manufacturer}{s.model ? ' ' + s.model : ''}</div>}
                  {s.installed_year && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '4px' }}>Installed {s.installed_year}</div>}
                  <div style={{ fontSize: '11px', color: s.status === 'good' ? C.success : C.amber, fontFamily: 'Calibri, sans-serif' }}>
                    {s.status === 'good' ? '✓ Up to date' : '⚠ Service due'}
                  </div>
                </div>
                )
              })}
              <div style={S.addCard} onClick={() => setShowAddSystem(true)}>
                <div style={{ fontSize: '24px', color: C.muted, marginBottom: '4px' }}>+</div>
                <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', textAlign: 'center' }}>Add system</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════ MAINTENANCE TAB ════════ */}
      {activeTab === 'maintenance' && (
        <div style={S.content} className="casavio-content">
          <div style={{ fontSize: '26px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>Maintenance</div>
          <div style={{ fontSize: '14px', color: C.muted, marginBottom: '28px' }}>
            {needsAttentionItems.length > 0
              ? `${needsAttentionItems.filter(t => t.status === 'overdue').length > 0 ? `${needsAttentionItems.filter(t => t.status === 'overdue').length} overdue · ` : ''}${needsAttentionItems.filter(t => t.status === 'soon').length} due soon`
              : 'Track and log completed maintenance tasks'}
          </div>

          {/* Fire Safety tile */}
          {(() => {
            const completedCount = fireCompletedItems.length
            const fireCompletionPct = FIRE_SAFETY_ITEMS.length > 0 ? Math.round((completedCount / FIRE_SAFETY_ITEMS.length) * 100) : 0
            return (
              <div
                onClick={() => setShowFireSafety(true)}
                style={{ background: C.card, borderRadius: 12, padding: '16px 20px', marginBottom: 16, cursor: 'pointer', border: fireCompletionPct < 50 ? '1px solid #DC2626' : fireCompletionPct < 80 ? `1px solid ${C.amber}` : '1px solid #16A34A', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(44,29,20,0.04)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 24 }}>🔥</span>
                  <div>
                    <div style={{ fontFamily: f, fontSize: 15, color: C.brown }}>Fire Safety Review</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2, fontFamily: 'Calibri, sans-serif' }}>
                      {fireChecklist ? `${completedCount}/${FIRE_SAFETY_ITEMS.length} items · Last updated ${new Date(fireChecklist.completed_at || fireChecklist.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : `Annual review recommended · ${completedCount}/${FIRE_SAFETY_ITEMS.length} complete`}
                    </div>
                    <div style={{ width: 180, height: 4, background: C.border, borderRadius: 2, marginTop: 6 }}>
                      <div style={{ width: `${fireCompletionPct}%`, height: '100%', background: fireCompletionPct < 50 ? '#DC2626' : fireCompletionPct < 80 ? C.amber : '#16A34A', borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                </div>
                <span style={{ color: C.muted, fontSize: 18 }}>›</span>
              </div>
            )
          })()}

          {/* Needs Attention */}
          {(loading || systemsLoading) ? (
            <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '13px', padding: '12px 0', marginBottom: '24px' }}>Loading...</div>
          ) : needsAttentionItems.length === 0 && upcomingItems.length === 0 && allMaintenanceItems.length === 0 ? (
            <div style={{ background: C.card, borderRadius: '14px', border: `1px solid ${C.border}`, padding: '40px 32px', textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}><Wrench size={36} strokeWidth={1} color={C.muted} /></div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>Add appliances to track maintenance</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px' }}>Your maintenance schedule will appear here once you add appliances or systems.</div>
              <button style={S.addBtn} onClick={() => setActiveTab('home_profile')}>Go to Home Profile</button>
            </div>
          ) : needsAttentionItems.length === 0 && neverStartedItems.length === 0 ? (
            <div style={{ background: '#EBF5EF', border: `1px solid ${C.success}33`, borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <CheckCheck size={28} strokeWidth={1.5} color={C.success} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.success }}>All caught up!</div>
                <div style={{ fontSize: '13px', color: C.success, fontFamily: 'Calibri, sans-serif', opacity: 0.8 }}>No tasks overdue or due in the next 30 days.</div>
              </div>
            </div>
          ) : (
            <>
              {needsAttentionItems.length > 0 && (
                <>
                  <div style={S.sectionTitle}>Needs Attention</div>
                  <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '24px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)', padding: '0 16px' }}>
                    {needsAttentionItems.map((item, i) => (
                      <MaintenanceTaskTile
                        key={`${item.itemType}-${item.itemId}-${item.task}`}
                        task={{ task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }}
                        nextDue={item.nextDue}
                        status={item.status}
                        isCompleted={false}
                        lastCompleted={item.lastCompleted}
                        applianceName={item.itemName}
                        showApplianceName={true}
                        onCheck={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                        onOpen={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                        onOrderNow={getOrderNow(item)}
                        onAddToJobs={item.taskType === 'contractor' ? () => addToWishlist({ jobTitle: `${item.task} — ${item.itemName}`, category: getTaskCategory(item.task), label: item.task, desc: `${item.itemName} · ${item.interval}`, source: 'maintenance_task' }) : undefined}
                        isLast={i === needsAttentionItems.length - 1}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Upcoming (31–90 days) */}
          {upcomingItems.length > 0 && (
            <>
              <div style={S.sectionTitle}>Coming Up (next 90 days)</div>
              <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '24px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)', padding: '0 16px' }}>
                {upcomingItems.map((item, i) => (
                  <MaintenanceTaskTile
                    key={`${item.itemType}-${item.itemId}-${item.task}`}
                    task={{ task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }}
                    nextDue={item.nextDue}
                    status={item.status}
                    isCompleted={false}
                    lastCompleted={item.lastCompleted}
                    applianceName={item.itemName}
                    showApplianceName={true}
                    onCheck={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOpen={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOrderNow={getOrderNow(item)}
                    onAddToJobs={item.taskType === 'contractor' ? () => addToWishlist({ jobTitle: `${item.task} — ${item.itemName}`, category: getTaskCategory(item.task), label: item.task, desc: `${item.itemName} · ${item.interval}`, source: 'maintenance_task' }) : undefined}
                    isLast={i === upcomingItems.length - 1}
                  />
                ))}
              </div>
            </>
          )}

          {/* Not Yet Started */}
          {neverStartedItems.length > 0 && (
            <>
              <div style={S.sectionTitle}>Not Yet Started</div>
              <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '24px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)', padding: '0 16px' }}>
                {neverStartedItems.map((item, i) => (
                  <MaintenanceTaskTile
                    key={`${item.itemType}-${item.itemId}-${item.task}`}
                    task={{ task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }}
                    nextDue={item.nextDue}
                    status={item.status}
                    isCompleted={false}
                    lastCompleted={null}
                    applianceName={item.itemName}
                    showApplianceName={true}
                    onCheck={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOpen={() => setSelectedTask({ taskObj: { task: item.task, interval: item.interval, type: item.taskType, partCost: item.partCost || null }, applianceName: item.itemName, applianceType: item.sourceType, itemId: item.itemId, itemType: item.itemType, nextDue: item.nextDue, status: item.status, lastCompleted: item.lastCompleted || null })}
                    onOrderNow={getOrderNow(item)}
                    onAddToJobs={item.taskType === 'contractor' ? () => addToWishlist({ jobTitle: `${item.task} — ${item.itemName}`, category: getTaskCategory(item.task), label: item.task, desc: `${item.itemName} · ${item.interval}`, source: 'maintenance_task' }) : undefined}
                    isLast={i === neverStartedItems.length - 1}
                  />
                ))}
              </div>
            </>
          )}

          {/* Maintenance Log */}
          <div style={S.sectionTitle}>Maintenance Log</div>
          {maintenanceLogsLoading ? (
            <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '13px', padding: '12px 0' }}>Loading...</div>
          ) : maintenanceLogs.length === 0 ? (
            <div style={{ background: C.card, borderRadius: '12px', border: `1px dashed ${C.border}`, padding: '28px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>No completed tasks yet. Mark tasks done to build your history.</div>
            </div>
          ) : (() => {
            const visibleLogs = showAllLogs ? maintenanceLogs : maintenanceLogs.slice(0, 5)
            return (
              <>
                <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}>
                  {visibleLogs.map((log, i) => {
                    const logType = log.item_type === 'appliance' ? appliances.find(a => a.id === log.item_id)?.type : systems.find(s => s.id === log.item_id)?.type
                    const LogIcon = getItemIcon(log.item_type, logType)
                    return (
                    <div key={log.id} style={{ padding: '12px 16px', borderBottom: i < visibleLogs.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <LogIcon size={20} strokeWidth={1.5} color={C.brown} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown }}>{log.task_name}</div>
                        <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{log.item_name}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>✓ {formatLogDate(log.completed_date)}</div>
                        {log.did_it_myself && <div style={{ fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif', fontWeight: '700', letterSpacing: '0.5px' }}>DIY ✓</div>}
                        {log.cost && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>${parseFloat(log.cost).toFixed(0)}</div>}
                      </div>
                    </div>
                    )
                  })}
                </div>
                {maintenanceLogs.length > 5 && (
                  <div
                    style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', cursor: 'pointer', padding: '4px 0' }}
                    onClick={() => setShowAllLogs(v => !v)}
                  >
                    {showAllLogs ? '↑ Show less' : `↓ View all ${maintenanceLogs.length} entries`}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* ════════ SERVICES TAB ════════ */}
      {activeTab === 'services' && (
        <div style={S.content} className="casavio-content">
          {/* ── Jobs section ── */}
          <div style={{ fontFamily: f, fontSize: '20px', color: C.brown, marginBottom: '2px' }}>Jobs</div>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '16px', fontFamily: 'Calibri, sans-serif' }}>Work orders, repairs, and wishlist</div>

          <button
            onClick={() => setShowAddJob(true)}
            style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, padding: '14px 0', fontFamily: f, fontSize: 15, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            + Add Job
          </button>

          {(() => {
            const URGENCY_ORDER = { emergency: 0, planned: 1, wishlist: 2, someday: 3 }
            const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 }
            const openJobs = jobs.filter(j => j.status !== 'complete')
            const completedJobs = jobs.filter(j => j.status === 'complete')
            const jobCounts = { emergency: 0, planned: 0, wishlist: 0, someday: 0, complete: completedJobs.length }
            openJobs.forEach(j => { if (jobCounts[j.urgency] !== undefined) jobCounts[j.urgency]++ })
            const baseFiltered = jobFilter === 'all' ? openJobs
              : jobFilter === 'complete' ? completedJobs
              : openJobs.filter(j => j.urgency === jobFilter)
            const sortedJobs = [...baseFiltered].sort((a, b) => {
              const urgencyDiff = (URGENCY_ORDER[a.urgency] || 0) - (URGENCY_ORDER[b.urgency] || 0)
              if (urgencyDiff !== 0) return urgencyDiff
              return (PRIORITY_ORDER[a.priority] || 1) - (PRIORITY_ORDER[b.priority] || 1)
            })

            const JobListItem = ({ job }) => {
              const urgency = URGENCY_CONFIG[job.urgency] || URGENCY_CONFIG.planned
              const category = JOB_CATEGORIES.find(c => c.id === job.category)
              return (
                <div
                  onClick={() => setSelectedJob(job)}
                  style={{ background: C.card, borderRadius: 12, padding: '14px 16px', marginBottom: 8, cursor: 'pointer', border: `1px solid ${C.border}`, borderLeft: `4px solid ${urgency.color}`, display: 'flex', alignItems: 'center', gap: 14 }}
                >
                  <div style={{ width: 40, height: 40, background: urgency.bg, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {category?.emoji || '🔨'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: f, fontSize: 15, color: C.brown, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: urgency.color, background: urgency.bg, padding: '2px 8px', borderRadius: 4 }}>
                        {urgency.emoji} {urgency.label}
                      </span>
                      {['wishlist', 'someday'].includes(job.urgency) && job.priority && (
                        <span style={{ fontSize: 11, color: job.priority === 'high' ? '#DC2626' : job.priority === 'medium' ? C.amber : C.muted }}>
                          {job.priority === 'high' ? '🔴 High' : job.priority === 'medium' ? '🟡 Medium' : '⚪ Low'}
                        </span>
                      )}
                      {job.est_cost_low && (
                        <span style={{ fontSize: 11, color: C.muted }}>
                          ~${Number(job.est_cost_low).toLocaleString()}{job.est_cost_high ? `–$${Number(job.est_cost_high).toLocaleString()}` : ''}
                        </span>
                      )}
                      {job.status && job.status !== 'new' && (
                        <span style={{ fontSize: 11, color: C.muted, background: C.bg, padding: '2px 8px', borderRadius: 4 }}>
                          {job.status.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    {job.notes && (
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.notes}</div>
                    )}
                  </div>
                  <div style={{ color: C.muted, fontSize: 18, flexShrink: 0 }}>›</div>
                </div>
              )
            }

            return (
              <>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '2px' }}>
                  {[{ id: 'all', label: 'All', emoji: '' }, { id: 'emergency', label: 'Emergency', emoji: '🔴' }, { id: 'planned', label: 'Planned', emoji: '🟡' }, { id: 'wishlist', label: 'Wishlist', emoji: '⭐' }, { id: 'someday', label: 'Someday', emoji: '💭' }, { id: 'complete', label: 'Completed', emoji: '✓' }].map(flt => (
                    <button key={flt.id} onClick={() => setJobFilter(flt.id)} style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid', borderColor: jobFilter === flt.id ? C.amber : C.border, background: jobFilter === flt.id ? C.amber : 'transparent', color: jobFilter === flt.id ? '#fff' : C.muted, cursor: 'pointer', fontSize: '12px', fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {flt.emoji && `${flt.emoji} `}{flt.label}{flt.id !== 'all' && jobCounts[flt.id] > 0 ? ` (${jobCounts[flt.id]})` : ''}
                    </button>
                  ))}
                </div>
                {jobsLoading ? (
                  <div style={{ color: C.muted, fontFamily: 'Calibri, sans-serif', fontSize: '13px', padding: '12px 0' }}>Loading...</div>
                ) : sortedJobs.length === 0 ? (
                  <>
                    <div style={{ background: C.card, borderRadius: 12, border: `1px dashed ${C.border}`, padding: '20px', textAlign: 'center', marginBottom: 16 }}>
                      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>No jobs yet. Tap + Add Job to get started.</div>
                    </div>
                    {jobFilter === 'wishlist' && (
                      <div>
                        <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' }}>Popular wishlist ideas</div>
                        {WISHLIST_SUGGESTIONS.map(s => (
                          <div key={s.jobTitle} style={{ background: C.card, borderRadius: 12, padding: '12px 16px', marginBottom: 8, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: f, fontSize: 14, color: C.brown, marginBottom: 2 }}>{s.label}</div>
                              <div style={{ fontSize: 11, color: C.muted }}>{s.desc}</div>
                              {s.estLow && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>~${s.estLow.toLocaleString()}–${s.estHigh.toLocaleString()}</div>}
                            </div>
                            <button
                              onClick={() => addToWishlist(s)}
                              style={{ background: 'none', border: `1px solid ${C.amber}`, borderRadius: 8, padding: '6px 12px', fontSize: 12, color: C.amber, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              + Add to wishlist
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    {sortedJobs.map(job => <JobListItem key={job.id} job={job} />)}
                  </div>
                )}
              </>
            )
          })()}

          {/* ── Recurring Services section ── */}
          {(recurringServices.length > 0 || contractorJobs.length > 0) && (
            <>
              <div style={{ ...S.sectionTitle, marginTop: '28px', marginBottom: '12px' }}>My Services</div>
              <div style={S.grid}>
                {services.map(s => (
                  <div key={s.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px', cursor: 'pointer' }} onClick={() => setSelectedService(s)}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>{s.icon || (s.service_type === 'recurring' ? '🔄' : '🔨')}</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '2px' }}>{s.name}</div>
                    {s.provider_name && <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '2px' }}>{s.provider_name}</div>}
                    {s.frequency && <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{s.frequency}</div>}
                    {s.cost && <div style={{ fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>${parseFloat(s.cost).toFixed(0)}{s.service_type === 'recurring' ? '/visit' : ''}</div>}
                  </div>
                ))}
                <div style={S.addCard} onClick={() => setShowAddService(true)}>
                  <div style={{ fontSize: '24px', color: C.muted, marginBottom: '4px' }}>+</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', textAlign: 'center' }}>Add service</div>
                </div>
              </div>
            </>
          )}

          {/* ── Secondary links ── */}
          <div style={{ borderTop: `1px solid ${C.bg}`, marginTop: '32px', paddingTop: '20px' }}>
            <div style={{ fontSize: '11px', color: C.muted, fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '12px' }}>Find Help</div>
            <div onClick={() => { setContractorDirCategory(''); setContractorDirListingType('contractor'); setShowContractorDir(true) }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${C.bg}`, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>🔨</span>
                <div>
                  <div style={{ fontSize: '15px', color: C.brown, fontFamily: f }}>Find a Contractor</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Verified North Bay professionals</div>
                </div>
              </div>
              <span style={{ color: C.muted, fontSize: '18px' }}>›</span>
            </div>
            <div onClick={() => setShowAddService(true)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>🔄</span>
                <div>
                  <div style={{ fontSize: '15px', color: C.brown, fontFamily: f }}>Add Recurring Service</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Landscaping, cleaning, pest control</div>
                </div>
              </div>
              <span style={{ color: C.muted, fontSize: '18px' }}>›</span>
            </div>
          </div>
        </div>
      )}


      {/* ════════ SETTINGS TAB ════════ */}
      {activeTab === 'settings' && (
        <div style={{ padding: '28px 24px', maxWidth: '580px', margin: '0 auto' }} className="casavio-content">
          <div style={{ fontSize: '26px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>Settings</div>
          <div style={{ fontSize: '14px', color: C.muted, marginBottom: '24px' }}>Manage your account and preferences</div>

          {/* Invite Someone */}
          <div
            style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}
            onClick={() => setShowInviteModal(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <UserPlus size={24} strokeWidth={1.5} color={C.amber} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>Invite Someone</div>
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Share access to your home</div>
              </div>
            </div>
            <div style={{ color: C.amber, fontSize: '16px' }}>→</div>
          </div>

          {/* My Account */}
          <div
            style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}
            onClick={() => setShowSettings(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <UserCircle size={24} strokeWidth={1.5} color={C.amber} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>My Account</div>
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Name, phone, email, password</div>
              </div>
            </div>
            <div style={{ color: C.amber, fontSize: '16px' }}>→</div>
          </div>

          {/* My Role — inline dropdown */}
          <div style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <Home size={24} strokeWidth={1.5} color={C.amber} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>My Role</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Your relationship to this property</div>
                </div>
              </div>
              <select
                value={userRelation}
                onChange={e => updateRelation(e.target.value)}
                style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: C.brown, background: C.bg, cursor: 'pointer', outline: 'none' }}
              >
                {RELATION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Emergency Sheet */}
          <div
            style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}
            onClick={() => setShowEmergencyWizard(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '22px', lineHeight: 1 }}>🆘</span>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>Emergency Sheet</div>
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Members, contacts, shutoffs & printable sheet</div>
              </div>
            </div>
            <div style={{ color: C.amber, fontSize: '16px' }}>→</div>
          </div>

          {/* Export */}
          <div
            style={{ background: C.card, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}
            onClick={() => setShowPrint(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <FileOutput size={24} strokeWidth={1.5} color={C.amber} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>Export Home Summary</div>
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Print or save a PDF summary</div>
              </div>
            </div>
            <div style={{ color: C.amber, fontSize: '16px' }}>→</div>
          </div>

          {/* Preferred Language */}
          <div style={{ background: '#FDFAF6', borderRadius: '12px', border: '1px solid #DDD4C6', padding: '18px 20px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(44,29,20,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '22px', lineHeight: 1 }}>🌐</span>
                <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>{t('settings.preferredLanguage')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {langSaved && <span style={{ fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif' }}>{t('settings.languageSaved')}</span>}
                <select
                  value={prefLang}
                  onChange={async (e) => {
                    const newLang = e.target.value
                    setPrefLang(newLang)
                    await supabase.from('profiles').update({ language_preference: newLang }).eq('id', session?.user?.id)
                    i18n.changeLanguage(newLang)
                    localStorage.setItem('casavio_preferred_language', newLang)
                    setLangSaved(true)
                    setTimeout(() => setLangSaved(false), 2000)
                  }}
                  style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: C.brown, background: C.bg, cursor: 'pointer', outline: 'none' }}
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
            </div>
          </div>

          {/* Legal links + affiliate disclosure */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px', marginBottom: '8px' }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', textDecoration: 'underline' }}>Privacy Policy</a>
            <span style={{ fontSize: '12px', color: C.border }}>·</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', textDecoration: 'underline' }}>Terms of Service</a>
          </div>
          <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px', lineHeight: '1.5' }}>
            As an Amazon Associate, Casavio earns from qualifying purchases.
          </div>

          <button
            style={{ width: '100%', background: 'transparent', border: `1px solid ${C.danger}33`, borderRadius: '10px', padding: '12px', color: C.danger, fontSize: '13px', cursor: 'pointer', fontFamily: f, letterSpacing: '1px', textTransform: 'uppercase', marginTop: '0' }}
            onClick={handleSignOut}
          >
            Sign Out
          </button>
        </div>
      )}

      {/* ── MODALS ── */}
      {showAdd && <AddAppliance userId={session?.user?.id} propertyId={activePropertyId} homeId={activeHomeId} initialType={addInitialType} onClose={() => { setShowAdd(false); setAddInitialType(null) }} onSaved={handleSaved} />}

      {showTourLauncher && (
        <div
          onClick={() => setShowTourLauncher(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#FDFAF6', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(44,29,20,0.28)' }}
          >
            <TourLauncher session={session} homeId={activeHomeId} onClose={() => setShowTourLauncher(false)} />
          </div>
        </div>
      )}
      {showAddSystem && <AddSystem userId={session?.user?.id} propertyId={activePropertyId} onClose={() => setShowAddSystem(false)} onSaved={handleSystemSaved} />}
      {showAddService && <AddService userId={session?.user?.id} propertyId={activePropertyId} onClose={() => setShowAddService(false)} onSaved={handleServiceSaved} />}
      {showAddProperty && <HomeSetup session={session} onComplete={handleSetupComplete} isAddingProperty />}
      {guidedAppliance && (
        <GuidedDocumentationModal
          appliance={guidedAppliance}
          onClose={() => setGuidedAppliance(null)}
          onUpdated={async () => { await fetchAppliances() }}
        />
      )}
      {selectedAppliance && (
        <ApplianceDetail appliance={selectedAppliance} onClose={() => setSelectedAppliance(null)}
          onPhotoUpdated={async () => { await fetchAppliances() }}
          onUpdated={async () => {
            // Refresh both the list AND the open modal's appliance, otherwise
            // InlineEditableField's useEffect reverts the typed value (it pulls
            // from the stale prop on the next render after editing closes).
            await fetchAppliances()
            if (selectedAppliance?.id) {
              const fresh = await fetchApplianceById(supabase, selectedAppliance.id)
              if (fresh) setSelectedAppliance(fresh)
            }
          }}
          onDeleted={() => { fetchAppliances(); setSelectedAppliance(null) }} />
      )}
      {selectedSystem && (
        <SystemDetail system={selectedSystem} onClose={() => setSelectedSystem(null)}
          onPhotoUpdated={async () => { await fetchSystems() }}
          onUpdated={() => { fetchSystems(); setSelectedSystem(null) }}
          onDeleted={() => { fetchSystems(); setSelectedSystem(null) }} />
      )}
      {selectedService && (
        <ServiceDetail service={selectedService} onClose={() => setSelectedService(null)}
          onUpdated={() => { fetchServices(); setSelectedService(null) }}
          onDeleted={() => { fetchServices(); setSelectedService(null) }} />
      )}
      {selectedTask && (
        <MaintenanceTaskModal
          task={selectedTask.taskObj}
          applianceName={selectedTask.applianceName}
          applianceType={selectedTask.applianceType}
          completedTasks={{}}
          taskDates={selectedTask.lastCompleted ? { [selectedTask.taskObj.task]: { lastCompleted: selectedTask.lastCompleted, nextDue: selectedTask.nextDue } } : {}}
          onComplete={handleTaskComplete}
          onDismiss={() => setSelectedTask(null)}
          onFindContractor={selectedTask.taskObj.type === 'contractor' ? () => { setContractorDirCategory(ITEM_TO_CONTRACTOR_CAT[selectedTask.applianceType] || ''); setShowContractorDir(true); setSelectedTask(null) } : null}
          onOrderNow={getOrderNow({ ...selectedTask.taskObj, taskType: selectedTask.taskObj.type, itemId: selectedTask.itemId, itemName: selectedTask.applianceName, task: selectedTask.taskObj.task })}
        />
      )}
      {showSettings && <SettingsPanel session={session} onClose={() => setShowSettings(false)} onProfileUpdated={handleProfileUpdated} activePropertyId={activePropertyId} initialSection={settingsSection} />}
      {showDocumentVault && <DocumentVault session={session} activePropertyId={activePropertyId} appliances={appliances} systems={systems} onClose={() => setShowDocumentVault(false)} />}
      {showContractorDir && <ContractorDirectory session={session} initialCategory={contractorDirCategory} initialListingType={contractorDirListingType} onClose={() => setShowContractorDir(false)} />}

      {selectedRoom && (
        <RoomDetail
          room={selectedRoom}
          homeId={activeHomeId}
          onSave={(updated) => {
            if (!updated) {
              setRooms(prev => prev.filter(r => r.id !== selectedRoom.id))
            } else {
              setRooms(prev => prev.map(r => r.id === updated.id ? updated : r))
            }
            setSelectedRoom(null)
          }}
          onClose={() => setSelectedRoom(null)}
        />
      )}
      {showAddRoom && (
        <AddRoom
          homeId={activeHomeId}
          onSave={(newRoom) => { setRooms(prev => [...prev, newRoom]); setShowAddRoom(false) }}
          onClose={() => setShowAddRoom(false)}
        />
      )}

      {/* Legacy modal — no longer reachable via UI as of 2026-05-10. Kept temporarily for rollback. Safe to delete after 2026-06-10 if no issues reported. */}
      {showEmergencyContacts && <EmergencyContacts homeId={activeHomeId} userId={session?.user?.id} onClose={() => setShowEmergencyContacts(false)} />}

      {showOnboarding && (
        <Onboarding
          supabase={supabase}
          session={session}
          profile={profile}
          homeId={activeHomeId}
          onComplete={() => { setShowOnboarding(false); fetchProperties() }}
        />
      )}

      {showScoreModal && <ScoreModal score={score} profile={profile} appliances={appliances} systems={systems} onClose={() => setShowScoreModal(false)} />}

      {showEnergyModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(44,29,20,0.4)' }} onClick={() => setShowEnergyModal(false)} />
          <div style={{ position: 'relative', background: C.card, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto', padding: '28px 24px 40px', boxShadow: '0 -8px 40px rgba(44,29,20,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: C.brown, fontFamily: f }}>Energy Score</div>
              <button onClick={() => setShowEnergyModal(false)} style={{ background: 'none', border: 'none', fontSize: '22px', color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '64px', fontWeight: '700', color: energyGradeColor, fontFamily: f, lineHeight: 1 }}>{energyGrade}</div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '8px' }}>
                {energyCertifiedCount} of {eligibleAppliances.length} eligible appliance{eligibleAppliances.length !== 1 ? 's' : ''} ENERGY STAR certified
              </div>
              {excludedAppliances.length > 0 && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>{excludedAppliances.length} appliance{excludedAppliances.length !== 1 ? 's' : ''} not rated by ENERGY STAR</div>}
              {energyTotalCost > 0 && <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>~${energyTotalCost}/yr estimated energy cost</div>}
            </div>
            <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' }}>Smart Home Devices</div>
            {[
              { key: 'smart_thermostat', label: 'Smart Thermostat', desc: 'Saves ~10% on heating & cooling', jobTitle: 'Install Smart Thermostat', category: 'smart_home', estLow: 150, estHigh: 300 },
              { key: 'smart_power_strips', label: 'Smart Power Strips', desc: 'Reduces standby power waste', jobTitle: 'Install Smart Power Strips', category: 'energy', estLow: 50, estHigh: 150 },
              { key: 'led_lighting', label: 'LED Lighting Throughout', desc: 'Up to 75% less energy than incandescent', jobTitle: 'Replace lighting with LED throughout', category: 'energy', estLow: 200, estHigh: 500 },
            ].map(({ key, label, desc, jobTitle, category, estLow, estHigh }) => {
              const val = !!(homeProfile?.[key])
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>{label}</div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{desc}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    {!val && (
                      <button
                        onClick={() => addToWishlist({ label, jobTitle, category, desc, estLow, estHigh })}
                        style={{ background: 'none', border: `1px solid ${C.amber}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, color: C.amber, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ＋ Wishlist
                      </button>
                    )}
                    <div
                      onClick={async () => {
                        if (!activeHomeId) return
                        await supabase.from('homes').update({ [key]: !val }).eq('id', activeHomeId)
                        fetchHomeProfile(activeHomeId)
                      }}
                      style={{ width: '44px', height: '24px', borderRadius: '12px', background: val ? C.success : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                    >
                      <div style={{ position: 'absolute', top: '2px', left: val ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                </div>
              )
            })}
            {eligibleAppliances.length > 0 && (
              <>
                <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif', marginTop: '20px', borderTop: `1px solid ${C.border}`, paddingTop: '20px' }}>Appliance Breakdown</div>
                {eligibleAppliances.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>{a.nickname || a.name}</div>
                      <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{a.make} {a.model}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {a.energy_star === true ? (
                        <div style={{ fontSize: '10px', color: C.success, background: '#EBF5EF', border: `1px solid ${C.success}44`, borderRadius: '10px', padding: '3px 8px', fontFamily: 'Calibri, sans-serif', fontWeight: '700' }}>
                          ENERGY STAR{a.energy_star_tier ? ` — ${a.energy_star_tier}` : ''}
                        </div>
                      ) : a.energy_star === false ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Not certified</div>
                          <button
                            onClick={() => addToWishlist({ jobTitle: `Upgrade to ENERGY STAR ${a.nickname || a.name}`, category: 'energy', label: `Upgrade ${a.nickname || a.name}`, desc: `Current ${a.nickname || a.name} is not ENERGY STAR certified`, estLow: 500, estHigh: 2000 })}
                            style={{ background: 'none', border: `1px solid ${C.amber}`, borderRadius: 6, padding: '3px 8px', fontSize: 10, color: C.amber, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            ＋ Wishlist
                          </button>
                        </div>
                      ) : (
                        <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic' }}>Not checked yet</div>
                      )}
                      {a.est_annual_energy_cost && (
                        <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>~${a.est_annual_energy_cost}/yr</div>
                      )}
                    </div>
                  </div>
                ))}
                {excludedAppliances.length > 0 && (
                  <>
                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: '16px', marginBottom: '8px', fontFamily: 'Calibri, sans-serif' }}>Not Rated by Energy Star</div>
                    {excludedAppliances.map(a => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{a.nickname || a.name}</div>
                          <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{a.make} {a.model}</div>
                        </div>
                        <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic' }}>No ENERGY STAR rating</div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {wishlistToast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: '#2C1D14', color: '#FDFAF6', borderRadius: 8, padding: '10px 20px', fontSize: 14, zIndex: 2000, whiteSpace: 'nowrap', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          ⭐ {wishlistToast} added to wishlist
        </div>
      )}

      {showFireSafety && (
        <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 1000, overflowY: 'auto', fontFamily: 'Calibri, sans-serif' }}>
          <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 0 60px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', background: C.card, borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, zIndex: 10 }}>
              <div>
                {activeFSCategory ? (
                  <button onClick={() => setActiveFSCategory(null)} style={{ background: 'none', border: 'none', color: C.amber, fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    ← Back
                  </button>
                ) : null}
                <div style={{ fontFamily: f, fontSize: 20, color: C.brown }}>
                  {activeFSCategory ? `${FIRE_CATEGORIES[activeFSCategory]?.emoji} ${FIRE_CATEGORIES[activeFSCategory]?.label}` : '🔥 Fire Safety Review'}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{fireCompletedItems.length}/{FIRE_SAFETY_ITEMS.length} items complete</div>
              </div>
              <button onClick={() => { setShowFireSafety(false); setActiveFSCategory(null) }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {!activeFSCategory ? (
                <>
                  {/* Category tile grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                    {Object.entries(FIRE_CATEGORIES).map(([catId, cat]) => {
                      const items = FIRE_SAFETY_ITEMS.filter(i => i.category === catId)
                      const completed = items.filter(i => fireCompletedItems.includes(i.id)).length
                      const pct = items.length > 0 ? completed / items.length : 0
                      return (
                        <div key={catId} onClick={() => setActiveFSCategory(catId)}
                          style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(44,29,20,0.05)' }}>
                          <div style={{ fontSize: 28, marginBottom: 6 }}>{cat.emoji}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.brown, marginBottom: 2 }}>{cat.label.split(' — ')[0]}</div>
                          {cat.label.includes(' — ') && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{cat.label.split(' — ')[1]}</div>}
                          <div style={{ fontSize: 11, color: completed === items.length ? C.success : C.muted, marginBottom: 6 }}>{completed}/{items.length} done</div>
                          <div style={{ height: 3, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 2, background: pct === 1 ? C.success : C.amber, width: `${pct * 100}%`, transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {/* Cal Fire link */}
                  <div style={{ background: '#FFF8E7', borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${C.amber}44` }}>
                    <div style={{ fontSize: 14, color: C.amber, fontWeight: 700, marginBottom: 4, fontFamily: f }}>🚒 Free Cal Fire Home Assessment</div>
                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Cal Fire offers free defensible space inspections for North Bay homeowners</div>
                    <a href="https://www.fire.ca.gov/programs/wildfire-preparedness/defensible-space-home-hardening/" target="_blank" rel="noopener noreferrer" style={{ color: C.amber, fontSize: 13 }}>
                      Schedule free inspection → calfire.ca.gov
                    </a>
                  </div>
                  <button onClick={saveFireChecklist} disabled={fireSaving} style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, padding: '16px 0', fontFamily: f, fontSize: 16, cursor: fireSaving ? 'default' : 'pointer' }}>
                    {fireSaving ? 'Saving...' : 'Save Progress'}
                  </button>
                </>
              ) : (
                /* Drill-down: category item list */
                (() => {
                  const catItems = FIRE_SAFETY_ITEMS.filter(i => i.category === activeFSCategory)
                  return (
                    <>
                      {catItems.map((item, idx) => {
                        const checked = fireCompletedItems.includes(item.id)
                        return (
                          <FireSafetyItem
                            key={item.id}
                            item={item}
                            checked={checked}
                            isLast={idx === catItems.length - 1}
                            onToggle={() => setFireCompletedItems(prev => checked ? prev.filter(id => id !== item.id) : [...prev, item.id])}
                            onAddWishlist={() => addToWishlist({ jobTitle: item.label, category: 'structural', label: item.label, desc: 'Fire safety improvement', estLow: 200, estHigh: 1000 })}
                          />
                        )
                      })}
                      <button onClick={saveFireChecklist} disabled={fireSaving} style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, padding: '16px 0', fontFamily: f, fontSize: 16, cursor: fireSaving ? 'default' : 'pointer', marginTop: 20 }}>
                        {fireSaving ? 'Saving...' : 'Save Progress'}
                      </button>
                    </>
                  )
                })()
              )}
            </div>
          </div>
        </div>
      )}
      {showPrint && <PrintSummary profile={profile} appliances={appliances} systems={systems} score={score} grade={grade} onClose={() => setShowPrint(false)} />}
      {logService && <ServiceLogModal service={logService} propertyId={activePropertyId} onClose={() => setLogService(null)} onSaved={() => { setLogService(null); fetchServices(); fetchServiceLogs() }} />}
      {showRequestModal && requestContractor && <ContractorRequestModal contractor={requestContractor} session={session} activeHomeId={activeHomeId} onClose={() => { setShowRequestModal(false); setRequestContractor(null) }} onSubmitted={() => { setShowRequestModal(false); setRequestContractor(null) }} />}
      {showInviteModal && <InviteModal session={session} homeId={activeHomeId} homeAddress={resolveDisplayAddress(profile)} onClose={() => setShowInviteModal(false)} />}

      {/* Add Job Modal */}
      {(showAddJob || editingJob) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => { setShowAddJob(false); setEditingJob(null); setJobStep(1); setJobData({ urgency: 'wishlist', priority: 'medium' }) }}>
          <div style={{ background: C.card, borderRadius: '20px 20px 0 0', padding: '24px 24px 40px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontFamily: f, fontSize: '20px', color: C.brown }}>{editingJob ? 'Edit Job' : 'Add Job'}</div>
              <button onClick={() => { setShowAddJob(false); setEditingJob(null); setJobStep(1); setJobData({ urgency: 'wishlist', priority: 'medium' }) }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted }}>×</button>
            </div>

            {/* Step 1 — urgency selection */}
            {jobStep === 1 && !editingJob && (
              <div>
                <div style={{ fontFamily: f, fontSize: '16px', color: C.brown, marginBottom: '16px' }}>What type of job is this?</div>
                {[
                  { id: 'emergency', emoji: '🔴', label: 'Emergency', desc: 'Needs attention now' },
                  { id: 'planned', emoji: '🟡', label: 'Planned', desc: 'Scheduled maintenance or repair' },
                  { id: 'wishlist', emoji: '⭐', label: 'Wishlist', desc: 'When time and budget allow' },
                  { id: 'someday', emoji: '💭', label: 'Someday', desc: 'Long-term dream or goal' },
                ].map(opt => (
                  <div key={opt.id} onClick={() => { setJobData(p => ({ ...p, urgency: opt.id })); setJobStep(2) }} style={{ background: jobData.urgency === opt.id ? '#FFF8E7' : C.card, border: `1px solid ${jobData.urgency === opt.id ? C.amber : C.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: f, fontSize: '15px', color: C.brown }}>{opt.label}</div>
                      <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{opt.desc}</div>
                    </div>
                    <div style={{ color: C.muted }}>›</div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 2 — job details */}
            {(jobStep === 2 || editingJob) && (() => {
              const jd = editingJob || jobData
              const setJd = editingJob ? (fn) => setEditingJob(p => typeof fn === 'function' ? fn(p) : { ...p, ...fn }) : (fn) => setJobData(p => typeof fn === 'function' ? fn(p) : { ...p, ...fn })
              return (
                <div>
                  {!editingJob && <button onClick={() => setJobStep(1)} style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: '14px', marginBottom: '16px', fontFamily: 'Calibri, sans-serif' }}>← Back</button>}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Category</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                      {JOB_CATEGORIES.map(cat => (
                        <div key={cat.id} onClick={() => setJd(p => ({ ...p, category: cat.id }))} style={{ background: jd.category === cat.id ? C.amber : C.bg, borderRadius: '8px', padding: '8px 4px', textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ fontSize: '18px' }}>{cat.emoji}</div>
                          <div style={{ fontSize: '10px', color: jd.category === cat.id ? '#fff' : C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px', lineHeight: 1.2 }}>{cat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', display: 'block', marginBottom: '6px' }}>What needs to be done? *</label>
                    <input value={jd.title || ''} onChange={e => setJd(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Install coded front door lock" style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '15px', fontFamily: 'Calibri, sans-serif', color: C.brown, boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                  {['wishlist', 'someday'].includes(jd.urgency) && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Priority</div>
                      {['high', 'medium', 'low'].map(p => (
                        <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                          <input type="radio" name="priority" value={p} checked={jd.priority === p} onChange={() => setJd(prev => ({ ...prev, priority: p }))} style={{ accentColor: C.amber }} />
                          <span style={{ fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>{p === 'high' ? '🔴 High — Do this soon' : p === 'medium' ? '🟡 Medium — When budget allows' : '⚪ Low — Someday maybe'}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Estimated Cost (optional)</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="number" value={jd.est_cost_low || ''} onChange={e => setJd(p => ({ ...p, est_cost_low: e.target.value }))} placeholder="From $" style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Calibri, sans-serif', color: C.brown, outline: 'none' }} />
                      <span style={{ color: C.muted, fontFamily: 'Calibri, sans-serif' }}>to</span>
                      <input type="number" value={jd.est_cost_high || ''} onChange={e => setJd(p => ({ ...p, est_cost_high: e.target.value }))} placeholder="To $" style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Calibri, sans-serif', color: C.brown, outline: 'none' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif', display: 'block', marginBottom: '6px' }}>Notes (optional)</label>
                    <textarea value={jd.notes || ''} onChange={e => setJd(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional details..." rows={3} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'Calibri, sans-serif', color: C.brown, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                  {editingJob ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={async () => {
                        const { error } = await supabase.from('jobs').update({ title: jd.title, category: jd.category, urgency: jd.urgency, priority: jd.priority, est_cost_low: jd.est_cost_low ? parseFloat(jd.est_cost_low) : null, est_cost_high: jd.est_cost_high ? parseFloat(jd.est_cost_high) : null, notes: jd.notes || null }).eq('id', jd.id)
                        if (!error) { setEditingJob(null); fetchJobs() }
                      }} style={{ flex: 2, background: C.amber, color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 0', fontFamily: f, fontSize: '15px', cursor: 'pointer' }}>Save Changes</button>
                      <button onClick={async () => { await supabase.from('jobs').delete().eq('id', jd.id); setEditingJob(null); fetchJobs() }} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '14px 0', fontSize: '13px', cursor: 'pointer', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Delete</button>
                    </div>
                  ) : (
                    <button onClick={saveJob} style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 0', fontFamily: f, fontSize: '16px', cursor: 'pointer', marginTop: '4px' }}>
                      {jd.urgency === 'emergency' ? '🔴 Report Emergency' : jd.urgency === 'planned' ? '🟡 Schedule Job' : jd.urgency === 'wishlist' ? '⭐ Add to Wishlist' : '💭 Save for Later'}
                    </button>
                  )}
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {selectedJob && !editingJob && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setSelectedJob(null)}
        >
          <div
            style={{ background: C.card, borderRadius: '20px 20px 0 0', padding: '24px 24px 40px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
              <div style={{ flex: 1, marginRight: '12px' }}>
                <div style={{ fontFamily: f, fontSize: '20px', color: C.brown, lineHeight: 1.3 }}>{selectedJob.title}</div>
                <div style={{ fontSize: '13px', color: (URGENCY_CONFIG[selectedJob.urgency] || URGENCY_CONFIG.planned).color, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>
                  {(URGENCY_CONFIG[selectedJob.urgency] || URGENCY_CONFIG.planned).emoji} {(URGENCY_CONFIG[selectedJob.urgency] || URGENCY_CONFIG.planned).label} · {JOB_CATEGORIES.find(c => c.id === selectedJob.category)?.label || 'General'}
                </div>
              </div>
              <button onClick={() => setSelectedJob(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, flexShrink: 0 }}>×</button>
            </div>

            {/* Cost */}
            {(selectedJob.est_cost_low || selectedJob.est_cost_high) && (
              <div style={{ background: C.bg, borderRadius: '10px', padding: '12px 16px', margin: '14px 0' }}>
                <div style={{ fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: 'Calibri, sans-serif', marginBottom: '4px' }}>Estimated Cost</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: C.brown, fontFamily: f }}>
                  ${selectedJob.est_cost_low ? Number(selectedJob.est_cost_low).toLocaleString() : '?'}
                  {selectedJob.est_cost_high ? ` – $${Number(selectedJob.est_cost_high).toLocaleString()}` : ''}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedJob.notes && (
              <div style={{ marginBottom: '16px', marginTop: '12px' }}>
                <div style={{ fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: 'Calibri, sans-serif', marginBottom: '6px' }}>Notes</div>
                <div style={{ fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>{selectedJob.notes}</div>
              </div>
            )}

            {/* Status pipeline */}
            <div style={{ marginBottom: '20px', marginTop: '14px' }}>
              <div style={{ fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: 'Calibri, sans-serif', marginBottom: '10px' }}>Status</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[{ id: 'new', label: 'New' }, { id: 'quoted', label: 'Quoted' }, { id: 'approved', label: 'Approved' }, { id: 'in_progress', label: 'In Progress' }, { id: 'complete', label: 'Complete' }].map(step => {
                  const statuses = ['new', 'quoted', 'approved', 'in_progress', 'complete']
                  const currentIdx = statuses.indexOf(selectedJob.status || 'new')
                  const stepIdx = statuses.indexOf(step.id)
                  const isActive = stepIdx === currentIdx
                  const isDone = stepIdx < currentIdx
                  return (
                    <div key={step.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={() => { updateJobStatus(selectedJob.id, step.id); setSelectedJob(p => ({ ...p, status: step.id })) }}>
                      <div style={{ width: '100%', height: '4px', borderRadius: '2px', background: isDone || isActive ? C.amber : C.border }} />
                      <div style={{ fontSize: '9px', color: isActive ? C.amber : C.muted, fontFamily: 'Calibri, sans-serif', textAlign: 'center', lineHeight: 1.2, fontWeight: isActive ? '700' : '400' }}>{step.label}</div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Find a Contractor */}
            {selectedJob.status !== 'complete' && (
              <button
                onClick={() => { const ct = JOB_CAT_TO_CONTRACTOR[selectedJob.category] || ''; setContractorDirCategory(ct); setContractorDirListingType('contractor'); setShowContractorDir(true); setSelectedJob(null) }}
                style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: '10px', padding: '14px 0', fontFamily: f, fontSize: '15px', cursor: 'pointer', marginBottom: '10px' }}
              >
                🔨 Find a Contractor
              </button>
            )}

            {/* Secondary buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {selectedJob.status !== 'complete' && (
                <button
                  onClick={() => { updateJobStatus(selectedJob.id, 'complete'); setSelectedJob(null) }}
                  style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 0', fontSize: '13px', cursor: 'pointer', color: C.success, fontFamily: 'Calibri, sans-serif' }}
                >
                  ✓ Mark Complete
                </button>
              )}
              <button
                onClick={() => { setEditingJob({ ...selectedJob }); setSelectedJob(null) }}
                style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 0', fontSize: '13px', cursor: 'pointer', color: C.brown, fontFamily: 'Calibri, sans-serif' }}
              >
                ✎ Edit
              </button>
            </div>

            {/* Delete */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={async () => { if (window.confirm('Delete this job?')) await deleteJob(selectedJob.id) }}
                style={{ background: 'none', border: 'none', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif', textDecoration: 'underline' }}
              >
                Delete job
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmergencyWizard && (
        <EmergencySheetWizard
          session={session}
          homeId={activeHomeId}
          userProfile={userProfile}
          homeAddress={resolveDisplayAddress(homes.find(h => h.id === activeHomeId) || profile)}
          onComplete={handleEmergencySetupComplete}
          onClose={() => setShowEmergencyWizard(false)}
        />
      )}

      {showProblemReport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setShowProblemReport(false)}>
          <div style={{ background: C.card, borderRadius: '20px 20px 0 0', padding: '28px 24px 40px', width: '100%', maxWidth: '480px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.brown, marginBottom: '6px' }}>Report a Bug</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '16px' }}>Help us improve Casavio.</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {[
                'Something is broken',
                'Page not loading',
                'Data not saving',
                'Display issue',
                'Notification issue',
                'Other feedback',
              ].map(cat => (
                <button
                  key={cat}
                  onClick={() => setProblemCategory(cat)}
                  style={{
                    padding: '8px 14px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer',
                    border: `1px solid ${problemCategory === cat ? C.amber : C.border}`,
                    background: problemCategory === cat ? '#FBF3E8' : C.bg,
                    color: problemCategory === cat ? C.amber : C.brown,
                    fontFamily: 'Calibri, sans-serif',
                  }}
                >{cat}</button>
              ))}
            </div>
            <textarea
              value={problemDescription}
              onChange={e => setProblemDescription(e.target.value)}
              placeholder="Describe what happened... (optional)"
              rows={3}
              style={{
                width: '100%', background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: '10px', padding: '10px 14px', fontSize: '14px',
                fontFamily: 'Calibri, sans-serif', color: C.brown, resize: 'vertical',
                boxSizing: 'border-box', outline: 'none', marginBottom: '12px',
              }}
            />
            <button
              onClick={submitProblemReport}
              disabled={!problemCategory || problemSubmitting}
              style={{
                width: '100%', padding: '14px',
                background: problemCategory ? C.amber : C.border,
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontFamily: 'Georgia, serif', cursor: problemCategory ? 'pointer' : 'default',
              }}
            >{problemSubmitting ? 'Sending…' : 'Send Report'}</button>
          </div>
        </div>
      )}
    </div>
  )
}